import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ApiErrorCode, createApiError } from '@rsmaids/shared/types/errors';
import { getBookingsByContactFromCache } from '@rsmaids/shared/services/api/bookingsCacheService';
import { useParams } from 'react-router-dom';
import {
  IonContent,
  IonPage,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonList,

  IonItem,
  IonLabel,
  IonIcon,
  IonButton,
  IonText,
  IonCol,
  IonSegment,
  IonSegmentButton,
  IonToggle,
  IonRadioGroup,
  IonRadio,
  useIonViewWillEnter,
  useIonRouter,
  IonActionSheet,
  IonRow,
  IonModal,
  IonGrid,
  IonRefresherContent,
  IonSkeletonText,
  IonTextarea,
  IonChip,
  IonCard,
  IonCardContent,
  IonAvatar,
  IonBadge,
  IonSpinner,
  IonInput,
} from '@ionic/react';
import {
  document,
  image,
  videocam,
  attach,
  chatbubbleOutline,
  ellipsisVertical,
  trashOutline,
  share,
  copyOutline,
  pencilOutline,
  cashOutline,
  documentTextOutline,
  closeCircle,
  closeOutline,
  caretDown,
  bagCheckOutline,
  createOutline,
  checkmarkCircleOutline,
  timeOutline,
  addCircleOutline,
  calendarOutline,
  cardOutline,
  chevronBack,
  mailOutline,
  businessOutline,
  briefcaseOutline,
  pinOutline,
  homeOutline,
  locationOutline,
  cubeOutline,
  refreshOutline,
  refreshCircleOutline,
  callOutline,
  phonePortraitOutline,
  pricetagOutline,
  listOutline,
  chevronDownOutline,
  searchOutline,
  informationCircleOutline,
  enterOutline,
} from 'ionicons/icons';
import { type Contact as ContactInterface, Label, ContactAttachment, ContactSubscription, CustomField, ContactEmail, ContactPhone, ContactAddress } from '@rsmaids/shared/types/contacts';
import InlineTypePicker from '../../components/InlineTypePicker';
import LocationPicker from '../../components/LocationPicker';
import MiniCalendarPicker from '../../components/MiniCalendarPicker';
import { PortalPickerDropdown } from '../../components/PortalPickerDropdown';
import { contactCallbacks } from './contactCallbacks';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import styles from './Contact.module.scss';
import BasePageWithHeader, { TitleCard } from '../../components/BasePageWithHeader';
import PositionedRefresher from '../../components/PositionedRefresher';
import { useScrollSnap } from '../../hooks/useScrollSnap';
import { useContactLiveSync } from '../../hooks/useContactLiveSync';
import { contactSavedToast, contactErrorToast } from '../../utils/contactToasts';
import { LabelSelectionModal } from '../../components/LabelsComponents/LabelModals';
import { TaskItem } from '@rsmaids/shared/types/tasks';
import TaskModal from '../../components/TaskModal';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { capitalizeFirstLetter } from '@rsmaids/shared/utils/stringUtils';
import { proxyImage, getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import { useIonToast, useIonAlert } from '@ionic/react';
import { getServiceColor, ServiceType } from '@rsmaids/shared/types/services';
import Booking from './BookingForm';
import BookingDetails from './BookingDetails';
import NewContact from './NewContact';
import { dbContacts } from '../../auth/firebase';
import GroupedInvoiceCard from '../../components/GroupedInvoiceCard';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { fetchZohoPricingData, fetchZohoPricelistItems, fetchZohoTaxes } from '../../services/zohoServiceOrderService';
import type { ZohoItem, ZohoPricelist, ZohoTax } from '../../services/zohoServiceOrderService';
import { updateContact as updateZohoContact } from '../../services/zohoInvoicingService';
import { getSubscriptions, getSubscriptionDetail, getHelcimCustomer, manageSubscription } from '../../services/helcimInvoicingService';
import InvoiceList from '../InvoiceList';
import type { HelcimSubscription, HelcimPaymentPlan } from '@rsmaids/shared/services/api/helcim/helcim';
import HelcimSubscriptionDetailModal from '../../components/HelcimSubscriptionDetailModal';
import HelcimSubscriptionsModal from '../../components/HelcimSubscriptionsModal';
import { placePhoneCall } from '../../utils/placePhoneCall';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../../i18n/dynamic';
import { intlLocale } from '../../i18n/locale';
import i18n from '../../i18n';

// Per-contact billing config stored in contactBilling/{contactId}. defaultRate
// is always "the final price the customer pays" regardless of isInclusiveTax —
// functions/src/services/zohoServiceOrderService.ts's createServiceOrder does
// the pre-tax conversion server-side when isInclusiveTax is false, using
// taxPercentage. Matches desktop's ContactDetailDesktop.tsx billing shape.
interface BillingConfig {
  pricelistId: string;
  pricelistName: string;
  itemId: string;
  itemName: string;
  defaultRate: string;
  isInclusiveTax: boolean;
  taxPreference: 'taxable' | 'exempt';
  taxId: string;
  taxName: string;
  taxPercentage: number;
  // How Complete invoices this customer: one invoice per sales order, grouped sales orders on
  // one invoice (billed later), or never from Complete.
  invoiceMode: 'perOrder' | 'grouped' | 'manual';
  // True only for clients billed by Helcim's own recurring-subscription engine
  // (separately from this app's Wix-booking SO flow). Changes what
  // defaultRate means when isInclusiveTax is false — see
  // functions/src/services/zohoServiceOrderService.ts's isHelcimSubscriber
  // doc comment for the full reasoning.
  isHelcimSubscriber: boolean;
}



// Matches HelcimSubscriptionsModal's STATUS_COLOR convention (same subscription
// data, same "Manage" flow) so the status badge looks the same everywhere.
const SUBSCRIPTION_STATUS_COLOR: Record<string, string> = {
  active: 'success',
  paused: 'tertiary',
  expired: 'medium',
  inactive: 'medium',
};

// Tone-class lookup for the badge — ion-badge's default (non `.ion-color`)
// :host rule reads --background/--color directly, so a CSS-module tone class
// is applied instead of Ionic's `color` prop, matching HelcimSubscriptionsModal's
// BADGE_TONE_CLASS convention.
const SUBSCRIPTION_BADGE_TONE_CLASS: Record<string, string> = {
  success: 'subscriptionStatusSuccess',
  tertiary: 'subscriptionStatusTertiary',
  medium: 'subscriptionStatusMedium',
};

const swallowControlTextClick = (e: React.SyntheticEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

function mapHelcimSubToContactSub(
  sub: HelcimSubscription,
  plans: HelcimPaymentPlan[],
  contactId: string
): ContactSubscription {
  const plan = plans.find(p => p.id === sub.paymentPlanId);
  // getSubscriptions({ skipEnrichment: true }) always returns plans: [] (the
  // "enrichment" it skips is per-subscription payment-history calls, not plan
  // lookup) — the real name is still resolved server-side and attached
  // directly on the subscription as paymentPlanName, so prefer that first.
  const planName = (sub['paymentPlanName'] as string | undefined) || plan?.name || `Plan #${sub.paymentPlanId ?? sub.id}`;
  let frequency: ContactSubscription['frequency'] = 'monthly';
  if (plan?.billingPeriod === 'weekly') {
    frequency = (plan.billingPeriodIncrements ?? 1) <= 1 ? 'weekly' : 'bi-weekly';
  }
  const statusMap: Record<string, ContactSubscription['status']> = {
    active: 'active', paused: 'paused', cancelled: 'expired', canceled: 'expired', inactive: 'inactive',
    term_ending: 'active', term_ended: 'expired',
  };
  const status = statusMap[sub.status?.toLowerCase() ?? ''] ?? 'active';
  const nextBillingDate = sub.dateBilling ? new Date(sub.dateBilling).toISOString() : new Date().toISOString();
  const billingDay = plan?.dateBilling;
  return {
    id: String(sub.id),
    contactId,
    name: planName,
    type: 'subscription',
    planName,
    status,
    nextDate: nextBillingDate,
    nextBillingDate,
    startDate: new Date(sub.dateActivated || Date.now()),
    amount: sub.recurringAmount ?? plan?.recurringAmount ?? 0,
    frequency,
    dayOfWeek: /^[A-Za-z]/.test(billingDay ?? '') ? billingDay : undefined,
    dayOfMonth: /^\d/.test(billingDay ?? '') ? billingDay : undefined,
    paymentMethod: sub.paymentMethod === 'bank' ? 'bank' : sub.paymentMethod === 'card' ? 'card' : undefined,
  };
}

// Parses a booking's effective start Date from whatever shape the API/cache handed
// back (Date instance, ISO string, or a plain "YYYY-MM-DD" date field) — ported from
// ContactDetailDesktop.tsx so the Client History stats below use the same notion of
// "when did this booking start" as the proven desktop implementation.
function parseBookingStart(booking: BookingItem): Date | null {
  if (booking.startTime instanceof Date) {
    return Number.isNaN(booking.startTime.getTime()) ? null : booking.startTime;
  }
  if (typeof booking.startTime === 'string' && booking.startTime) {
    const parsedStart = new Date(booking.startTime);
    if (!Number.isNaN(parsedStart.getTime())) return parsedStart;
  }
  if (typeof booking.date === 'string' && booking.date) {
    const ymdMatch = booking.date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymdMatch) {
      return new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]), 12, 0, 0, 0);
    }
  }
  return null;
}

// Ported from ContactDetailDesktop.tsx — treats a booking as counting toward Client
// History unless it's explicitly cancelled/refunded (an empty/unknown status is
// treated as confirmed-like rather than excluded).
function isConfirmedLikeBooking(booking: BookingItem): boolean {
  const status = String(booking.status || '').toLowerCase();
  if (!status) return true;
  if (status === 'cancelled' || status === 'canceled' || status === 'refunded') return false;
  return (
    status === 'confirmed' ||
    status === 'booked' ||
    status === 'rescheduled' ||
    status === 'pending' ||
    status === 'due' ||
    status === 'payment_awaiting'
  );
}

// Ported from ContactDetailDesktop.tsx — a contact's bookings can come back under more
// than one alias ID (route id / _id / wixContactId), so results fetched per-alias are
// merged and de-duped by booking id (falling back to a contactId+start+end composite key
// for bookings without one) before counting/filtering them.
function dedupeBookings(bookings: BookingItem[]): BookingItem[] {
  const map = new Map<string, BookingItem>();
  bookings.forEach((booking) => {
    const key = booking.id || `${booking.contactId}-${String(booking.startTime)}-${String(booking.endTime)}`;
    if (!map.has(key)) map.set(key, booking);
  });
  return Array.from(map.values());
}

// Self-contained editable rows for the Info tab's Phone/Email/Address sections.
// Use InlineTypePicker (in-box type dropdown) / LocationPicker to match
// NewContact's edit form.
const PhoneEditRow: React.FC<{
  phone: ContactPhone;
  index: number;
  portalRef: React.RefObject<HTMLElement | null>;
  onChange: (phone: ContactPhone) => void;
  onDelete: () => void;
}> = ({ phone, index, portalRef, onChange, onDelete }) => {
  const { t: tr } = useTranslation();
  return (
    <InlineTypePicker
      mode="phone"
      label={index === 0 ? tr('pages.bookingSystem.Contact.primaryPhone') : tr('pages.bookingSystem.Contact.additionalPhone')}
      portalRef={portalRef}
      inputType="tel"
      placeholder={tr('pages.bookingSystem.Contact.phoneNumber')}
      value={phone.phone || ''}
      onValueChange={(v) => onChange({ ...phone, phone: v })}
      currentType={phone.tag || 'mobile'}
      onTypeChange={(type) => onChange({ ...phone, tag: type })}
      onDelete={onDelete}
    />
  );
};

const EmailEditRow: React.FC<{
  email: ContactEmail;
  index: number;
  portalRef: React.RefObject<HTMLElement | null>;
  onChange: (email: ContactEmail) => void;
  onDelete: () => void;
}> = ({ email, index, portalRef, onChange, onDelete }) => {
  const { t: tr } = useTranslation();
  return (
    <InlineTypePicker
      mode="email"
      label={index === 0 ? tr('pages.bookingSystem.Contact.primaryEmail') : tr('pages.bookingSystem.Contact.additionalEmail')}
      portalRef={portalRef}
      inputType="email"
      placeholder={tr('pages.bookingSystem.Contact.emailAddress')}
      value={email.email || ''}
      onValueChange={(v) => onChange({ ...email, email: v })}
      currentType={email.tag || 'personal'}
      onTypeChange={(type) => onChange({ ...email, tag: type })}
      onDelete={onDelete}
    />
  );
};

const AddressEditRow: React.FC<{
  address: ContactAddress;
  index: number;
  portalRef: React.RefObject<HTMLElement | null>;
  onChange: (address: ContactAddress) => void;
  onDelete: () => void;
}> = ({ address, index, portalRef, onChange, onDelete }) => {
  const { t: tr } = useTranslation();
  const addr = address.address || { addressLine1: '', addressLine2: '', city: '', subdivision: '', postalCode: '', country: '' };
  return (
    <IonList className={styles.addressEditBlock}>
      <InlineTypePicker
        mode="address"
        label={index === 0 ? tr('pages.bookingSystem.Contact.primaryAddress') : tr('pages.bookingSystem.Contact.address', { v1: index + 1 })}
        portalRef={portalRef}
        placeholder={tr('pages.bookingSystem.Contact.streetAddress')}
        value={addr.addressLine1 || ''}
        onValueChange={(v) => onChange({ ...address, address: { ...addr, addressLine1: v } })}
        currentType={address.tag || 'home'}
        onTypeChange={(type) => onChange({ ...address, tag: type })}
        onDelete={onDelete}
      />
      <IonItem lines="none" className={styles.editEntry}>
        <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.aptSuiteOptional')}</IonLabel>
        <IonInput
          value={addr.addressLine2 || ''}
          placeholder={tr('pages.bookingSystem.Contact.apartmentSuiteUnit')}
          onIonInput={(e) => onChange({ ...address, address: { ...addr, addressLine2: e.detail.value as string } })}
        />
      </IonItem>
      <IonItem lines="none" className={styles.editEntry}>
        <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.city')}</IonLabel>
        <IonInput
          value={addr.city || ''}
          placeholder={tr('pages.bookingSystem.Contact.city')}
          onIonInput={(e) => onChange({ ...address, address: { ...addr, city: e.detail.value as string } })}
        />
      </IonItem>
      <LocationPicker
        label={tr('pages.bookingSystem.Contact.stateProvince')}
        type="state"
        placeholder={tr('pages.bookingSystem.Contact.selectState')}
        portalRef={portalRef}
        inputId={`address-state-${address.id || index}`}
        value={addr.subdivision || ''}
        onChange={(value) => onChange({ ...address, address: { ...addr, subdivision: value } })}
      />
      <IonItem lines="none" className={styles.editEntry}>
        <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.zipPostalCode')}</IonLabel>
        <IonInput
          value={addr.postalCode || ''}
          placeholder={tr('pages.bookingSystem.Contact.zipPostalCode2')}
          onIonInput={(e) => onChange({ ...address, address: { ...addr, postalCode: e.detail.value as string } })}
        />
      </IonItem>
      <LocationPicker
        label={tr('pages.bookingSystem.Contact.country')}
        type="country"
        placeholder={tr('pages.bookingSystem.Contact.selectCountry')}
        portalRef={portalRef}
        inputId={`address-country-${address.id || index}`}
        value={addr.country || ''}
        onChange={(value) => onChange({ ...address, address: { ...addr, country: value } })}
      />
    </IonList>
  );
};

interface PortalDropdownOption {
  label: string;
  value: string;
  sublabel?: string;
}

interface EllipsisMenuItem {
  label: string;
  value: string;
  icon?: string;
  danger?: boolean;
}

// Same anchored-portal mechanism as PriceCalculator.tsx's usePickerDropdown/
// PickerDropdown (proven, no item-count cap needed there) — portals into an
// in-page portalRef container (absolute-positioned relative to it) instead of
// document.body, with the double-rAF read this codebase already found
// necessary since ion-item can still be mid-upgrade (shadow content not yet
// rendered) on the very first paint after mounting.
type PortalDropdownRect = { top: number; left?: number; right?: number; width?: number };

function usePortalDropdownPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  // Form-field pickers (pricelist/item/condition) anchor from the left edge
  // and stretch to match the field's width. A small icon-button anchor (e.g.
  // the history search-by-date button) has neither a sensible width to match
  // nor room to grow rightward without running off screen, so it instead
  // hangs from the anchor's right edge sized to its own content.
  align: 'left' | 'right' = 'left',
  matchWidth: boolean = true,
) {
  const [rect, setRect] = useState<PortalDropdownRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): PortalDropdownRect | null => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();

      // Flip upward when there isn't enough room below the anchor but there's
      // more room above it — otherwise a low-on-page anchor (e.g. a field deep
      // inside a scrolled card) always opens downward and runs off the bottom
      // of the viewport (or under the bottom tab bar) regardless of how little
      // space remains there. Only trusts the card's OWN measured height (never
      // available space, which says nothing about how tall the dropdown's
      // actual content is) — before that's known (first paint), always opens
      // downward; the ResizeObserver below corrects the position as soon as
      // the card's real height is measurable, whenever that actually happens.
      // --tab-bar-bottom-clearance is a calc() expression — getPropertyValue on
      // a custom property returns the raw unresolved string ("calc(64px + ...)"),
      // not a used pixel value, so it can't be parsed directly. The actual tab
      // bar element (rendered fixed/sticky at the bottom of the page) is used
      // instead: whatever space it visually occupies is what's unavailable.
      const tabBar = globalThis.document.querySelector('ion-tab-bar');
      const tabBarHeight = tabBar ? tabBar.getBoundingClientRect().height : 0;
      const spaceBelow = window.innerHeight - tabBarHeight - box.bottom;
      const spaceAbove = box.top;
      const cardHeight = cardRef.current?.getBoundingClientRect().height ?? 0;
      const shouldFlipUp = cardHeight > 0 && spaceBelow < cardHeight + 4 && spaceAbove > spaceBelow;

      const r: PortalDropdownRect = shouldFlipUp
        ? { top: box.top - anchor.top - cardHeight - 4 }
        : { top: box.bottom - anchor.top + 4 };
      if (matchWidth) r.width = box.width;
      if (align === 'right') r.right = anchor.right - box.right;
      else r.left = box.left - anchor.left;
      return r;
    };
    const applyRect = (r: PortalDropdownRect) => {
      const card = cardRef.current as HTMLElement | null;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.right = r.right !== undefined ? `${r.right}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    // The card doesn't exist in the DOM at all until `rect` is non-null (the
    // component returns null while `!rect`), so cardRef.current is guaranteed
    // null right here — the ResizeObserver can only start watching it once
    // React has actually committed it, which happens some time after
    // `setRect(initial)` below triggers a re-render. Polled on rAF rather than
    // watched via a second effect/ref-callback to keep this self-contained.
    const resizeObserver = new ResizeObserver(() => {
      const r = computeRect();
      if (r) { setRect(r); applyRect(r); }
    });
    let attachRaf = 0;
    const tryAttachObserver = () => {
      if (cardRef.current) { resizeObserver.observe(cardRef.current); return; }
      attachRaf = requestAnimationFrame(tryAttachObserver);
    };
    tryAttachObserver();

    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    const handleScroll = () => { const r = computeRect(); if (r) applyRect(r); };
    const handleResize = () => { const r = computeRect(); if (r) { setRect(r); applyRect(r); } };
    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      onClose();
    };
    // Bare `document` is shadowed file-wide by the `document` icon imported
    // from ionicons/icons above — globalThis reaches the real DOM document
    // regardless of that shadowing (`window` isn't shadowed, but globalThis
    // keeps both lookups consistent here).
    const doc = globalThis.document;
    const win = globalThis.window;
    doc.addEventListener('scroll', handleScroll, true);
    win.addEventListener('resize', handleResize);
    doc.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancelAnimationFrame(attachRaf);
      resizeObserver.disconnect();
      doc.removeEventListener('scroll', handleScroll, true);
      win.removeEventListener('resize', handleResize);
      doc.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, anchorRef, portalRef, cardRef, onClose, align, matchWidth]);

  return rect;
}

const PortalDropdown: React.FC<{
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  options: PortalDropdownOption[];
  value?: string;
  onSelect: (value: string) => void;
  searchable?: boolean;
  emptyText?: string;
  align?: 'left' | 'right';
  matchWidth?: boolean;
}> = ({ isOpen, anchorRef, portalRef, onClose, options, value, onSelect, searchable, emptyText = i18n.t('pages.bookingSystem.Contact.noResults'), align = 'left', matchWidth = true }) => {
  const { t: tr } = useTranslation();
  const [search, setSearch] = useState('');
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePortalDropdownPosition(isOpen, anchorRef, portalRef, cardRef, onClose, align, matchWidth);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);

  // Word-by-word, symbol-insensitive matching (any order) so users can type
  // partial terms like "500 studio weekly" without matching exact punctuation.
  const normalizeSearch = useCallback((s: string) =>
    s.toLowerCase().replace(/[^a-z0-9.]/g, ' ').replace(/\s+/g, ' ').trim(),
    []);

  const matchesSearch = useCallback((text: string, query: string) => {
    const q = normalizeSearch(query);
    if (!q) return true;
    const textTokens = normalizeSearch(text).split(' ').filter(Boolean);
    const queryTokens = q.split(' ').filter(Boolean);

    return queryTokens.every((qt) => {
      const isNumeric = /^\d+(?:\.\d+)?$/.test(qt);
      if (isNumeric) return textTokens.includes(qt);
      return textTokens.some(tt => tt.startsWith(qt));
    });
  }, [normalizeSearch]);

  // Clear the search box the moment the dropdown closes — computed during
  // render rather than as a setState call inside an effect.
  const [wasSearchDropdownOpen, setWasSearchDropdownOpen] = useState(isOpen);
  if (isOpen !== wasSearchDropdownOpen) {
    setWasSearchDropdownOpen(isOpen);
    if (!isOpen && search) setSearch('');
  }
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);

  if (!isOpen || !rect || !portalNode) return null;

  const filtered = searchable && search.trim()
    ? options.filter(o => matchesSearch(`${o.label} ${o.sublabel ?? ''}`, search))
    : options;

  return createPortal(
    <IonCard ref={cardRef} className={styles.customDropdownList} style={{ ...rect, position: 'absolute' }}>
      {searchable && (
        <IonItem lines="none" className={styles.customDropdownSearchWrap}>
          <IonIcon icon={searchOutline} slot="start" />
          <IonInput
            className={styles.customDropdownSearchInput}
            placeholder={tr('pages.bookingSystem.Contact.search')}
            value={search}
            onIonInput={(e) => setSearch(String(e.detail.value ?? ''))}
          />
        </IonItem>
      )}
      <IonList lines="none" className={styles.customDropdownListInner}>
        {filtered.length === 0 ? (
          <IonItem lines="none" className={styles.customDropdownEmpty}>
            <IonLabel>{emptyText}</IonLabel>
          </IonItem>
        ) : (
          filtered.map(opt => (
            <IonItem
              key={opt.value}
              button
              detail={false}
              lines="none"
              className={`${styles.customDropdownItem} ${opt.value === value ? styles.customDropdownItemActive : ''}`}
              onClick={() => onSelect(opt.value)}
            >
              <IonLabel>{opt.label}</IonLabel>
              {opt.sublabel && <IonText slot="end" className={styles.customDropdownSublabel}>{opt.sublabel}</IonText>}
            </IonItem>
          ))
        )}
      </IonList>
    </IonCard>,
    portalNode,
  );
};

// Booking history is paged in from Wix on demand back to this date, one
// 90-day chunk at a time.
const OLDEST_HISTORY_DATE = new Date(2024, 6, 1);

// `contactId` + `onClose` embed this page in a modal (e.g. over Booking Details) instead of
// using it as a routed page: the id comes from the prop, back closes the modal, and the page
// loads its data on mount (the Ionic page lifecycle it normally loads from doesn't fire in a modal).
const Contact: React.FC<{ contactId?: string; onClose?: () => void }> = ({ contactId, onClose }) => {
  const { t: tr } = useTranslation();
  const ionRouter = useIonRouter();
  const routeParams = useParams<{ id: string }>();
  const id = (contactId ?? routeParams.id) as string;
  const embedded = !!onClose;
  // loadContact is declared much further below (it's a large useCallback);
  // useIonViewWillEnter's callback below calls it via this ref instead of a
  // direct reference to avoid a forward-reference / stale-closure hazard.
  const loadContactRef = useRef<() => void>(() => { });
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const titleCardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLIonGridElement>(null);
  // How far the list scrolls before the contact card is fully out of view (its height plus its bottom margin).
  // Measured, not fixed: a fixed 66px finished the card's fade/shrink while most of the card was still on screen,
  // leaving an empty, shrunken card shell above the sticky tabs.
  const [titleCollapseDistance, setTitleCollapseDistance] = useState(66);
  // Portal root for the Info tab's inline-edit pickers (InlineTypePicker /
  // LocationPicker / birthdate calendar).
  const infoPickerPortalRef = useRef<HTMLDivElement>(null);
  const [infoBirthdateOpen, setInfoBirthdateOpen] = useState(false);
  const infoBirthdateFieldRef = useRef<HTMLIonButtonElement>(null);
  const historyHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const historyTitleCardRef = useRef<HTMLIonCardElement>(null);
  const historyScrollRef = useRef<HTMLIonGridElement>(null);
  const innerScrollRef = useRef<HTMLDivElement>(null);
  const wasAtTop = useRef(true);
  const [refresherDisabled, setRefresherDisabled] = useState(false);
  const [contactDetails, setContactDetails] = useState<ContactInterface | null>(null);
  const [contact, setContact] = useState<ContactInterface | null>(null);
  // Email-only contacts (no firstName/lastName) would otherwise render a blank
  // name everywhere — fall back to company, then the first email's prefix.
  const contactDisplayName = useMemo(() => {
    if (!contact) return '';
    const full = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    if (full) return full;
    if (contact.company) return contact.company;
    const email = contact.emails?.[0]?.email;
    return email ? email.split('@')[0] : '';
  }, [contact]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState('details');
  const [availableLabels, setAvailableLabels] = useState<Label[]>([]);
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const contactService = DataServiceFactory.getService();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | undefined>();
  // Ellipsis actions menu — fixed-position glass dropdown, copied verbatim
  // from ServiceOrderDetail (menuOpen / menuBtnRef / menuCardRef + a
  // pointerdown outside-close effect + a CSS-positioned .ellipsisMenu).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef<HTMLIonButtonElement>(null);
  const menuCardRef = useRef<HTMLDivElement>(null);
  const menuBackdropRef = useRef<HTMLDivElement>(null);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const [upcomingBookings, setUpcomingBookings] = useState<BookingItem[]>([]);
  const [pastBookings, setPastBookings] = useState<BookingItem[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState<boolean>(false);
  // Older booking history (before the Firestore cache's ~90-day retention) is not
  // fetched up front — it's paged in from Wix on demand as the user scrolls the
  // Booking History modal, one 90-day chunk at a time, oldest cursor tracked here.
  const olderHistoryCursorRef = useRef<Date>(new Date(new Date().setDate(new Date().getDate() - 90)));
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<ContactSubscription[]>([]);
  const [rawHelcimPlans, setRawHelcimPlans] = useState<HelcimPaymentPlan[]>([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState<boolean>(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("30");


  // ✅ ADD: Add history stats state to parent
  const [historyStats, setHistoryStats] = useState({
    totalServices: 0,
    totalSpent: 0
  });
  // Client History stats are now computed by a dedicated effect (below, ported from
  // ContactDetailDesktop.tsx) that's keyed on [id, selectedPeriod, ...alias ids] rather
  // than by loadContact()'s Promise.all — this ref lets the unrelated "cache the complete
  // contact to localStorage" block in loadContact() snapshot the latest computed value
  // without loadContact() itself depending on (and re-running for) historyStats changes.
  const historyStatsRef = useRef(historyStats);
  useEffect(() => { historyStatsRef.current = historyStats; }, [historyStats]);
  const [isLoadingStats, setIsLoadingStats] = useState<boolean>(false);
  const [externalIds, setExternalIds] = useState({ helcimCustomerCode: '', zohoContactId: '' });
  const [billing, setBilling] = useState<BillingConfig>({
    pricelistId: '', pricelistName: '', itemId: '', itemName: '', defaultRate: '',
    isInclusiveTax: true, taxPreference: 'taxable', taxId: '', taxName: '', taxPercentage: 0, invoiceMode: 'perOrder',
    isHelcimSubscriber: false,
  });

  // Independent of loadContact()'s cached snapshot — contactBilling/{id} is its
  // own Firestore doc that can change without the rest of the contact changing
  // (e.g. an admin edits Tax Handling on desktop while this page's 5-minute
  // localStorage cache is still fresh and loadContact() never runs). Called
  // both from loadContact() (fresh page load) and from the cache-hit path in
  // useIonViewWillEnter below (so billing is never left at stale/default
  // values just because the rest of the page served from cache).
  const fetchBillingConfig = useCallback((contactId: string) => {
    getDoc(doc(dbContacts, 'contactBilling', contactId)).then(snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      setBilling({
        pricelistId: (d['defaultPricelistId'] as string) || '',
        pricelistName: (d['defaultPricelistName'] as string) || '',
        itemId: (d['defaultItemId'] as string) || '',
        itemName: (d['defaultItemName'] as string) || '',
        defaultRate: typeof d['defaultRate'] === 'number' ? String(d['defaultRate']) : '',
        isInclusiveTax: d['isInclusiveTax'] !== false,
        taxPreference: (d['taxPreference'] as 'taxable' | 'exempt') || 'taxable',
        taxId: (d['taxId'] as string) || '',
        taxName: (d['taxName'] as string) || '',
        taxPercentage: (d['taxPercentage'] as number) || 0,
        invoiceMode: (d['invoiceMode'] === 'grouped' || d['invoiceMode'] === 'manual') ? d['invoiceMode'] : 'perOrder',
        isHelcimSubscriber: d['isHelcimSubscriber'] === true,
      });
    }).catch(() => { /* non-blocking, same as before */ });
  }, []);

  // Inline contact-field editing (Info tab) — mirrors the desktop contact page's
  // per-section edit-in-place model instead of pushing every edit into a separate modal.
  const [contactFormData, setContactFormData] = useState<Partial<ContactInterface>>({});
  const [editingInfoSection, setEditingInfoSection] = useState<string | null>(null);
  // Latest editing state for the Firestore snapshot callback (which is created
  // once per `id` and can't see fresh state via closure).
  const editingInfoSectionRef = useRef<string | null>(null);
  useEffect(() => { editingInfoSectionRef.current = editingInfoSection; }, [editingInfoSection]);
  const [isSavingContactInfo, setIsSavingContactInfo] = useState(false);
  const [systemIdsExpanded, setSystemIdsExpanded] = useState(false);


  useEffect(() => {
    if (id) {
      contactCallbacks[id] = (updatedContact: ContactInterface) => {
        setContact(updatedContact);
      };

      return () => {
        delete contactCallbacks[id];
      };
    }
  }, [id]);

  const getAttachmentIcon = (type: string): string => {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('image') || lowerType.includes('jpg') || lowerType.includes('png')) {
      return image;
    } else if (lowerType.includes('video')) {
      return videocam;
    } else if (lowerType.includes('doc') || lowerType.includes('pdf') || lowerType.includes('txt')) {
      return document;
    } else {
      return attach;
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Add a helper function to format the date consistently
  const formatDateSafe = (dateValue: string | Date): string => {
    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString(intlLocale());
    }
    return new Date(dateValue).toLocaleDateString(intlLocale());
  };

  const formatTimeToString = (time: string | Date): string => {
    if (typeof time === 'string') {
      return time;
    }
    return time.getHours().toString().padStart(2, '0') + ':' +
      time.getMinutes().toString().padStart(2, '0');
  };

  const openMaps = (address: string) => {
    const encodedAddress = encodeURIComponent(address);

    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      // On iOS, try to use Apple Maps first
      window.open(`maps://maps.apple.com/?q=${encodedAddress}`, '_system');
    } else {
      // On Android and other platforms, use Google Maps
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_system');
    }
  };



  // Remove per-contact label fetching. Use only the global label list for availableLabels.


  const enterContact = () => {
    if (!id) return;

    // Check cache first (with expiration)
    const cached = localStorage.getItem(`contact-${id}`);
    const cacheTime = localStorage.getItem(`contact-${id}-time`);
    const now = Date.now();
    const cacheExpiry = 5 * 60 * 1000; // 5 minutes

    if (cached && cacheTime && (now - parseInt(cacheTime)) < cacheExpiry) {
      try {
        const data = JSON.parse(cached);
        console.log('Using cached contact data');
        setContact(data);
        setContactDetails(data);
        setUpcomingBookings(data.upcomingBookings || []);
        setPastBookings(data.pastBookings || []);
        setSubscriptions(data.subscriptions || []);
        setHistoryStats(data.historyStats || { totalServices: 0, totalSpent: 0 });
        setTasks(data.tasks || []);
        // Billing config (contactBilling/{id}) is fetched independently of the
        // rest of this cached snapshot — it's a separate Firestore doc that can
        // change without the contact itself changing (e.g. an admin edits Tax
        // Handling from the desktop app while this page's 5-minute cache is
        // still fresh). Restore a cached copy if the Billing Config Save
        // handler previously patched one in (see its own cache-patch below)
        // so switching tabs and back doesn't show stale defaults; either way,
        // kick off a fresh contactBilling fetch here too, since a cache hit
        // means loadContact() — and its own contactBilling fetch — never runs.
        if (data.billing) setBilling(data.billing);
        fetchBillingConfig(id);
        setIsLoading(false);
        setIsLoadingBookings(false);
        setIsLoadingSubscriptions(false);
        setIsLoadingStats(false);
        setIsLoadingTasks(false);
        return;
      } catch (error) {
        // Cache corrupted, fall through to load fresh
        console.log('Cache corrupted, loading fresh data:', error);
      }
    }

    // No cache or expired cache - load fresh data
    console.log('Loading contact data for:', id);
    loadContactRef.current();
  };
  useIonViewWillEnter(enterContact);
  // Embedded in a modal there is no page lifecycle, so load once on mount instead. (Kept in a
  // ref so the effect only re-runs when the contact changes, as the lifecycle hook did.)
  const enterContactRef = useRef(enterContact);
  useEffect(() => {
    enterContactRef.current = enterContact;
  });
  useEffect(() => {
    if (embedded) enterContactRef.current();
  }, [embedded, id]);

  // Live Firestore listener — keeps the contact's core fields (name, phones,
  // emails, addresses, company, jobTitle, birthdate, custom fields, labels,
  // member status) fresh in real time as this app / Zoho sync / Helcim sync /
  // contactSyncTrigger write back, instead of showing the stale localStorage
  // snapshot after an edit. Bookings/subscriptions/stats still come from
  // loadContact()'s one-time fetch.
  // Shared with the desktop page (useContactLiveSync): also holds a just-saved edit over the older Firestore
  // snapshots that arrive before the Wix webhook has caught up, so it doesn't snap back.
  const { pinSaved } = useContactLiveSync(
    id,
    contactService.contacts,
    (fresh) => {
      setContact(prev => (prev ? { ...prev, ...fresh } : fresh));
      setContactDetails(prev => (prev ? { ...prev, ...fresh } : fresh));
      try {
        const cached = localStorage.getItem(`contact-${id}`);
        const base = cached ? JSON.parse(cached) : {};
        localStorage.setItem(`contact-${id}`, JSON.stringify({ ...base, ...fresh }));
        localStorage.setItem(`contact-${id}-time`, Date.now().toString());
      } catch { /* cache write is best-effort */ }
    },
    // Don't clobber a section the user is actively editing.
    () => !!editingInfoSectionRef.current,
  );

  // Replace the broken functions with this working version:
  const applyGlobalOrderToContactFields = useCallback(async (contactFields: CustomField[]): Promise<CustomField[]> => {
    if (!contactFields || contactFields.length === 0) return [];

    try {
      const globalOrder = await contactService.getGlobalFieldOrder();

      if (globalOrder.length === 0) return contactFields; // No global order set

      // Sort THIS contact's fields according to global order
      return contactFields.sort((a, b) => {
        const aIndex = globalOrder.indexOf(a.fieldKey || a.label);
        const bIndex = globalOrder.indexOf(b.fieldKey || b.label);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    } catch (error) {
      createApiError(
        ApiErrorCode.SERVER_ERROR,
        'Error applying global field order',
        undefined,
        { originalError: error }
      );
      return contactFields;
    }
  }, [contactService]);

  // Pages in one older 90-day chunk of booking history from Wix, on demand —
  // called when the user scrolls near the bottom of the Booking History modal.
  // The Firestore cache (loaded up front in loadContact) only covers the last
  // ~90 days, so anything older has to come from Wix directly.
  const loadMoreHistory = useCallback(async () => {
    if (!id || isLoadingMoreHistory || !hasMoreHistory) return;

    const chunkEnd = new Date(olderHistoryCursorRef.current);
    chunkEnd.setDate(chunkEnd.getDate() - 1);
    if (chunkEnd <= OLDEST_HISTORY_DATE) {
      setHasMoreHistory(false);
      return;
    }

    const chunkStart = new Date(chunkEnd);
    chunkStart.setDate(chunkStart.getDate() - 89);
    const effectiveStart = chunkStart < OLDEST_HISTORY_DATE ? OLDEST_HISTORY_DATE : chunkStart;

    setIsLoadingMoreHistory(true);
    try {
      const older = await contactService.bookings.getBookingsByContactIdAndDateRange(id, effectiveStart, chunkEnd);
      olderHistoryCursorRef.current = effectiveStart;
      if (effectiveStart <= OLDEST_HISTORY_DATE) setHasMoreHistory(false);

      if (older.length > 0) {
        setPastBookings(prev => {
          const merged = new Map(prev.map(b => [b.id, b]));
          (older as BookingItem[]).forEach(b => { if (!merged.has(b.id)) merged.set(b.id, b); });
          return Array.from(merged.values()).sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
        });
      }
    } catch {
      // Leave hasMoreHistory as-is so scrolling again retries the same chunk
    } finally {
      setIsLoadingMoreHistory(false);
    }
  }, [id, isLoadingMoreHistory, hasMoreHistory, contactService]);

  const isLoadingDetailsData = isLoadingBookings || isLoadingSubscriptions || isLoadingStats || isLoadingTasks;

  // ✅ GOOD: Simple segment switching without API calls
  const handleSegmentChange = useCallback((e: CustomEvent) => {
    const newSegment = e.detail.value;
    setSelectedSegment(newSegment);
    // That's it! No API calls needed
  }, []);

  // ✅ GOOD: Load all data once in loadContact
  const loadContact = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setIsLoadingBookings(true);
    setIsLoadingSubscriptions(true);
    setIsLoadingStats(true);
    setIsLoadingTasks(true);

    try {
      // Hero (avatar/name/quick actions) only needs contactInfo — kick this off
      // immediately and render it the moment it resolves, instead of waiting on
      // the rest of the Promise.all below (bookings/subscriptions/stats/etc.),
      // which can take much longer. Shared with the Promise.all below (not
      // fetched twice) — that block still needs the same contactInfo for
      // field-ordering/attachments, but the hero doesn't have to wait for it.
      const contactInfoPromise = contactService.contacts.getContactById(id);
      contactInfoPromise.then(contactInfo => {
        if (contactInfo) setContact(prev => ({ ...contactInfo, attachments: prev?.attachments }));
      }).catch(() => { });

      // Await Firestore contact doc first to get Helcim customerCode for subscriptions
      let helcimCustomerCode = '';
      try {
        const contactSnap = await getDoc(doc(dbContacts, 'contacts', id));
        console.log('[Contact] Firestore snap exists:', contactSnap.exists(), 'fields:', contactSnap.exists() ? Object.keys(contactSnap.data()) : []);
        if (contactSnap.exists()) {
          const d = contactSnap.data();
          helcimCustomerCode = (d['customerCode'] as string) || '';
          console.log('[Contact] helcimCustomerCode:', helcimCustomerCode);
          setExternalIds({
            helcimCustomerCode,
            zohoContactId: (d['zohoContactId'] as string) || '',
          });
        }
      } catch (e) { console.error('[Contact] Firestore error:', e); }

      // Load per-contact billing config from contactBilling collection (non-blocking)
      fetchBillingConfig(id);

      // Fetch legacy notes1/2/3 in the background — this hits Wix Admin API directly
      // (no Firestore fast-path) and must never block the rest of the page load.
      contactService.contacts.getContactDetails(id)
        .then(setContactDetails)
        .catch(() => { });

      // Load ALL data needed for both segments in parallel
      const _t0 = performance.now();
      const _time = <T,>(label: string, p: Promise<T>): Promise<T> =>
        p.then(v => { console.log(`[Contact][timing] ${label}: ${(performance.now() - _t0).toFixed(0)}ms`); return v; });
      const [contactInfo, allBookingsRaw, helcimSubsResult, clientTasks, attachments] = await Promise.all([
        _time('getContactById', contactInfoPromise),
        // Only the Firestore cache (~90 days retention) loads up front. Older history
        // is paged in from Wix on demand as the user scrolls the Booking History modal
        // — see loadMoreHistory().
        _time('bookings', getBookingsByContactFromCache(id).catch(() => [] as BookingItem[])),
        _time('subscriptions', helcimCustomerCode
          ? getSubscriptions({ customerCode: helcimCustomerCode, skipEnrichment: true })
            .then(r => { console.log('[Contact] Helcim subs result:', r.subscriptions.length, 'subs'); return r; })
            .catch(e => { console.error('[Contact] getSubscriptions error:', e); return { subscriptions: [], plans: [] }; })
          : Promise.resolve({ subscriptions: [], plans: [] })),
        // Client History stats (Total Services / Total Spent) are computed separately —
        // see the useEffect keyed on [id, selectedPeriod, ...alias ids] below, ported
        // from ContactDetailDesktop.tsx — not fetched here.
        _time('tasks', contactService.tasks.getContactTasks(id).catch(() => [])),
        _time('attachments', contactService.attachments.getAttachments(id).catch(() => []))
      ]);
      console.log(`[Contact][timing] ALL DONE: ${(performance.now() - _t0).toFixed(0)}ms`);

      const rawSubs = helcimSubsResult.subscriptions || [];
      const rawPlans = helcimSubsResult.plans || [];
      console.log('[Contact] subscriptions raw count:', rawSubs.length);
      const subscriptionsData = rawSubs.map(sub => mapHelcimSubToContactSub(sub, rawPlans, id));

      // Apply field ordering (needs contactInfo.customFields as input, so can't join the Promise.all above)
      if (contactInfo?.customFields) {
        contactInfo.customFields = await applyGlobalOrderToContactFields(contactInfo.customFields);
      }

      // Set all data at once
      setContact(contactInfo ? { ...contactInfo, attachments } : contactInfo);
      setSubscriptions(subscriptionsData);
      setRawHelcimPlans(rawPlans);
      setTasks(clientTasks);

      // Reset older-history pagination for this contact — the Firestore cache
      // above only covers the last ~90 days, older pages are loaded on scroll.
      olderHistoryCursorRef.current = new Date(new Date().setDate(new Date().getDate() - 90));
      setHasMoreHistory(true);

      // Bookings from API are already filtered for this contact and do not include blocked/cancelled
      // Match dashboard logic: upcoming = today (local) to 7 days ahead
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const fourteenDays = new Date(today);
      fourteenDays.setDate(today.getDate() + 14);
      const upcoming: BookingItem[] = [];
      const past: BookingItem[] = [];
      (allBookingsRaw as BookingItem[]).forEach(booking => {
        let bookingStartTime: Date;
        if (booking.startTime instanceof Date) {
          bookingStartTime = booking.startTime;
        } else if (typeof booking.startTime === 'string') {
          bookingStartTime = new Date(booking.startTime);
        } else {
          // fallback: skip invalid
          return;
        }
        if (isNaN(bookingStartTime.getTime())) {
          return;
        }
        if (bookingStartTime >= today && bookingStartTime < fourteenDays) {
          upcoming.push(booking);
        } else if (bookingStartTime < today) {
          past.push(booking);
        }
        // Bookings 14+ days in future are not shown in upcoming, but could be added to a future section if needed
      });
      setUpcomingBookings(upcoming.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
      setPastBookings(past.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()));

      // Set all loading states to false only after ALL operations are complete
      setIsLoading(false);
      setIsLoadingBookings(false);
      setIsLoadingSubscriptions(false);
      setIsLoadingStats(false);
      setIsLoadingTasks(false);

      // Cache the complete contact with all data for next visit (after attachments are loaded)
      setTimeout(() => {
        setContact(current => {
          if (current && id) {
            const completeContact = {
              ...current,
              upcomingBookings: upcoming,
              pastBookings: past,
              subscriptions: subscriptionsData,
              historyStats: historyStatsRef.current,
              tasks: clientTasks
            };
            localStorage.setItem(`contact-${id}`, JSON.stringify(completeContact));
            localStorage.setItem(`contact-${id}-time`, Date.now().toString());
          }
          return current;
        });
      }, 100);

    } catch (error) {
      createApiError(
        ApiErrorCode.CONTACT_FETCH_FAILED,
        'Error loading contact',
        undefined,
        { originalError: error }
      );
    } finally {
      setIsLoading(false);
      // Ensure all loading states are false (safety net)
      setIsLoadingBookings(false);
      setIsLoadingSubscriptions(false);
      setIsLoadingStats(false);
      setIsLoadingTasks(false);
    }
  }, [id, applyGlobalOrderToContactFields, contactService.attachments, contactService.contacts, contactService.tasks, fetchBillingConfig]);
  useEffect(() => {
    loadContactRef.current = loadContact;
  }, [loadContact]);

  // Client History stats (Total Services / Total Spent) — ported from
  // ContactDetailDesktop.tsx's dedicated useEffect (ContactDetailDesktop.tsx ~lines
  // 434-464). Recomputes whenever the period dropdown changes, unlike the old
  // getContactHistoryStats() call this replaced (which only ran once, inside
  // loadContact()'s Promise.all, and never reacted to selectedPeriod). Bookings are
  // fetched across every known alias ID for this contact (route id / contact._id /
  // contact.wixContactId) since a contact's bookings can be recorded under more than
  // one ID across systems — same defensive approach as desktop's bookingContactIds.
  const bookingContactIds = useMemo(() => {
    return Array.from(new Set([
      id,
      contact?.id,
      contact?._id,
      contact?.wixContactId,
    ].filter((v): v is string => typeof v === 'string' && v.trim().length > 0)));
  }, [id, contact?.id, contact?._id, contact?.wixContactId]);

  useEffect(() => {
    if (bookingContactIds.length === 0) return;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const cutoff = selectedPeriod === 'all' ? new Date('2015-01-01') : (() => {
      const d = new Date(); d.setDate(d.getDate() - parseInt(selectedPeriod, 10)); return d;
    })();

    const zohoId = externalIds.zohoContactId;
    const zohoInvoicing = zohoId
      ? httpsCallable<Record<string, unknown>, { rows: Record<string, unknown>[]; total: number }>(getFunctions(), 'zohoInvoicing')
      : null;

    let cancelled = false;

    Promise.all([
      // Wix bookings count across all contact ID aliases
      Promise.all(bookingContactIds.map(cid =>
        contactService.bookings.getBookingsByContactIdAndDateRange(cid, cutoff, today).catch(() => [] as BookingItem[])
      )).then(results => {
        const all = dedupeBookings(results.flat()).filter(isConfirmedLikeBooking)
          .filter(b => { const s = parseBookingStart(b); return !!s && s >= cutoff && s < today; });
        return all.length;
      }),
      // Zoho total spent
      zohoInvoicing
        ? zohoInvoicing({ action: 'salesByCustomer', customer_id: zohoId, date_start: cutoff.toISOString().slice(0, 10), date_end: today.toISOString().slice(0, 10) })
          .then(res => res.data.total).catch(() => 0)
        : Promise.resolve(0),
    ]).then(([totalServices, totalSpent]) => {
      if (cancelled) return;
      setHistoryStats({ totalServices, totalSpent });
    });

    return () => { cancelled = true; };
  }, [id, selectedPeriod, bookingContactIds, externalIds.zohoContactId, contactService]);

  // ✅ ADD: Add refresh guard state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ✅ CHANGE: Update handleRefresh to prevent multiple simultaneous calls
  const handleRefresh = async (event: CustomEvent) => {
    // ✅ ADD: Guard against multiple simultaneous refreshes
    if (isRefreshing) {
      event.detail.complete();
      return;
    }

    // Starting refresh

    try {
      setIsRefreshing(true);
      await loadContact();
    } catch (error) {
      createApiError(
        ApiErrorCode.SERVER_ERROR,
        'Error during refresh',
        undefined,
        { originalError: error }
      );
    } finally {
      setIsRefreshing(false);
      event.detail.complete();
    }
  };

  const handleAssignLabels = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!contact) return;

    // Check cache first for instant opening
    const cacheKey = 'cached-labels';
    const timestampKey = 'cached-labels-timestamp';
    const cachedLabels = localStorage.getItem(cacheKey);
    const cacheTimestamp = localStorage.getItem(timestampKey);

    if (cachedLabels && cacheTimestamp) {
      const cacheAge = Date.now() - parseInt(cacheTimestamp);
      if (cacheAge < 30 * 60 * 1000) {
        // Use cached data - open immediately
        setAvailableLabels(JSON.parse(cachedLabels));
        setIsLabelModalOpen(true);
        return;
      }
    }

    // No valid cache - fetch fresh data before opening
    try {
      const allLabels = await contactService.labels.getAllLabels();
      setAvailableLabels(allLabels);

      // Update cache for next time
      localStorage.setItem(cacheKey, JSON.stringify(allLabels));
      localStorage.setItem(timestampKey, Date.now().toString());

      setIsLabelModalOpen(true);
    } catch (error) {
      createApiError(
        ApiErrorCode.SERVER_ERROR,
        'Error fetching all labels for modal',
        undefined,
        { originalError: error }
      );
    }
  };

  const handleSaveLabels = async (selectedLabelIds: string[]) => {
    if (!contact) {
      // Cannot save labels - contact is null
      return;
    }


    try {
      // **IMMEDIATELY update the local contact state for instant UI feedback**
      setContact(prevContact => {
        if (!prevContact) return null;
        return {
          ...prevContact,
          labels: selectedLabelIds
        };
      });

      // Compute which labels to remove and which to add
      const oldLabelIds = (contact.labels || []);
      const labelsToRemove = oldLabelIds.filter(id => !selectedLabelIds.includes(id));
      const labelsToAdd = selectedLabelIds.filter(id => !oldLabelIds.includes(id));

      // Remove labels that are no longer selected
      if (labelsToRemove.length > 0) {
        const removeSuccess = await contactService.labels.removeLabelsFromContact(contact.id, labelsToRemove);
        if (!removeSuccess) {
          throw new Error('Failed to remove labels');
        }
      }

      // Add new labels
      if (labelsToAdd.length > 0) {
        const addSuccess = await contactService.labels.assignLabelsToContact(contact.id, labelsToAdd);
        if (!addSuccess) {
          throw new Error('Failed to assign labels');
        }
      }

      pinSaved({ ...contact, labels: selectedLabelIds }, contact);
      presentToast(contactSavedToast(contact, { ...contact, labels: selectedLabelIds }, tr));
    } catch (error) {
      createApiError(
        ApiErrorCode.CONTACT_UPDATE_FAILED,
        'Error updating labels',
        undefined,
        { originalError: error }
      );
      // The failure used to be swallowed here, so the sheet closed as if it had saved: log it, put the old labels back
      // (they were already swapped in above for instant feedback) and tell the user
      console.error('[Contact] saving labels failed:', error);
      const previousLabels = contact.labels || [];
      setContact(prevContact => (prevContact ? { ...prevContact, labels: previousLabels } : null));
      presentToast(contactErrorToast(tr('pages.bookingSystem.Contact.failedToSaveChanges')));
    }
  };
  const handleSaveContactInfo = async () => {
    if (!contact?.id) return;
    setIsSavingContactInfo(true);
    try {
      const merged = { ...contact, ...contactFormData } as ContactInterface;
      if (contactFormData.jobTitle !== undefined) merged.position = contactFormData.jobTitle;
      await contactService.contacts.updateContact(merged);
      if (contactFormData.displayNameSource !== undefined && contactFormData.displayNameSource !== contact.displayNameSource) {
        await contactService.contacts.updateDisplayNameSource(contact.id, contactFormData.displayNameSource || '');
      }
      pinSaved(merged, contact);
      setContact(merged);
      setEditingInfoSection(null);
      setContactFormData({});
      presentToast(contactSavedToast(contact, merged, tr));
    } catch (error) {
      createApiError(ApiErrorCode.CONTACT_UPDATE_FAILED, 'Error updating contact', undefined, { originalError: error });
      presentToast(contactErrorToast(tr('pages.bookingSystem.Contact.failedToSaveChanges')));
    } finally {
      setIsSavingContactInfo(false);
    }
  };

  const handleCancelInfoEdit = () => {
    setContactFormData({});
    setEditingInfoSection(null);
  };

  const handleRemoveInfoField = async (fieldType: 'company' | 'position' | 'birthdate') => {
    if (!contact?.id) return;
    const patch: Partial<ContactInterface> = {};
    if (fieldType === 'company') patch.company = '';
    if (fieldType === 'position') { patch.jobTitle = ''; patch.position = ''; }
    if (fieldType === 'birthdate') patch.birthdate = '';
    const updated = { ...contact, ...patch } as ContactInterface;
    try {
      await contactService.contacts.updateContact(updated);
      pinSaved(updated, contact);
      setContact(updated);
      setContactFormData(prev => ({ ...prev, ...patch }));
      if (editingInfoSection === fieldType) setEditingInfoSection(null);
      presentToast(contactSavedToast(contact, updated, tr));
    } catch (error) {
      createApiError(ApiErrorCode.CONTACT_UPDATE_FAILED, 'Error removing field', undefined, { originalError: error });
      presentToast(contactErrorToast(tr('pages.bookingSystem.Contact.failedToRemoveField')));
    }
  };

  const handleRemoveDisplayNameSource = async () => {
    if (!contact?.id) return;
    try {
      await contactService.contacts.updateDisplayNameSource(contact.id, '');
      pinSaved({ ...contact, displayNameSource: undefined }, contact);
      setContact(prev => (prev ? { ...prev, displayNameSource: undefined } : prev));
      setContactFormData(prev => ({ ...prev, displayNameSource: undefined }));
      if (editingInfoSection === 'displayNameSource') setEditingInfoSection(null);
      presentToast(contactSavedToast(contact, { ...contact, displayNameSource: undefined }, tr));
    } catch (error) {
      createApiError(ApiErrorCode.CONTACT_UPDATE_FAILED, 'Error removing display name source', undefined, { originalError: error });
      presentToast(contactErrorToast(tr('pages.bookingSystem.Contact.failedToSaveChanges')));
    }
  };

  const handleShareContact = () => {
    // Share contact clicked
    // Implement share functionality
    if (navigator.share) {
      navigator.share({
        title: contactDisplayName,
        text: `Contact information for ${contactDisplayName}`,
        // You would need to generate a vCard or similar format
      }).catch(error => {
        createApiError(
          ApiErrorCode.SERVER_ERROR,
          'Error sharing contact',
          undefined,
          { originalError: error }
        );
      });
    } else {
      // Web Share API not supported
      // Fallback sharing method
    }
  };
  const primaryPhone = contact?.phones?.[0]?.phone;
  const handleCopyPhone = () => {
    // Copy phone clicked
    if (primaryPhone) {
      navigator.clipboard.writeText(primaryPhone)
        .then(() => {/* Phone number copied */ })
        .catch(err => {
          const _apiError = createApiError(
            ApiErrorCode.SERVER_ERROR,
            'Could not copy text',
            undefined,
            { originalError: err }
          );
          // Optionally show a toast or handle as needed
        });
    }
  };

  const handleDeleteContact = () => {
    presentAlert({
      header: tr('pages.bookingSystem.Contact.deleteContact'),
      message: tr('pages.bookingSystem.Contact.areYouSureYouWantTo', { contactDisplayName }),
      cssClass: 'glass-alert',
      buttons: [
        { text: tr('pages.bookingSystem.Contact.cancel'), role: 'cancel' },
        {
          text: tr('pages.bookingSystem.Contact.delete'),
          role: 'destructive',
          handler: () => {
            contactService.contacts.deleteContact(contact?.id || '')
              .then(() => {
                localStorage.setItem('contactListNeedsRefresh', 'true');
                if (onClose) onClose();
                else ionRouter.push('/tabs/contacts', 'back', 'pop');
              })
              .catch(() => {
                presentToast({ message: tr('pages.bookingSystem.Contact.failedToDeleteContact'), duration: 3000, color: 'danger' });
              });
          }
        }
      ]
    });
  };

  const contactMenuOptions: EllipsisMenuItem[] = [
    { label: tr('pages.bookingSystem.Contact.editContact'), value: "edit", icon: pencilOutline },
    { label: tr('pages.bookingSystem.Contact.shareContact'), value: "share", icon: share },
    { label: tr('pages.bookingSystem.Contact.copyPhoneNumber'), value: "copyPhone", icon: copyOutline },
    { label: tr('pages.bookingSystem.Contact.deleteContact'), value: "delete", icon: trashOutline, danger: true },
  ];

  const runContactMenuItem = (value: string) => {
    setMenuOpen(false);
    switch (value) {
      case 'edit':
        if (contact) setShowEditContactModal(true);
        break;
      case 'share':
        handleShareContact();
        break;
      case 'copyPhone':
        handleCopyPhone();
        break;
      case 'delete':
        handleDeleteContact();
        break;
    }
  };

  const toggleMenu = () => setMenuOpen(o => !o);

  useEffect(() => {
    if (!menuOpen) return;
    // `document` is shadowed file-wide by the ionicons `document` import above.
    const doc = globalThis.document;
    const handler = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuBtnRef.current?.contains(t)) return;
      if (menuCardRef.current?.contains(t)) return;
      // Backdrop taps are handled by the backdrop's own onClick (which keeps it
      // mounted through the click so the tap can't fall through to the page).
      if (menuBackdropRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    doc.addEventListener('pointerdown', handler);
    return () => doc.removeEventListener('pointerdown', handler);
  }, [menuOpen]);

  // Update the handleAddAttachment function
  const handleAddAttachment = async () => {
    if (!contact) return;

    const input: HTMLInputElement = window.document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png';

    input.onchange = async (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;

      const newAttachments: ContactAttachment[] = [];

      // Process each file individually
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          // Call addAttachments with a single file (correct parameter type)
          const attachment = await contactService.attachments.addAttachments(contact.id, file);

          if (attachment) {
            newAttachments.push(attachment);
          }
        } catch (error) {
          createApiError(
            ApiErrorCode.SERVER_ERROR,
            `Error uploading file ${file.name}`,
            undefined,
            { originalError: error }
          );
        }
      }

      // Update the contact with new attachments
      if (newAttachments.length > 0) {
        setContact(prev => {
          if (!prev) return null;
          return {
            ...prev,
            attachments: [...(prev.attachments || []), ...newAttachments]
          };
        });
      }
    };

    input.click();
  };

  // Add delete handler
  const handleDeleteAttachment = async (e: React.MouseEvent, attachmentId: string) => {
    e.stopPropagation(); // Prevent opening the attachment
    setShowDeleteConfirm(attachmentId);
  };

  // Extend ContactAttachment to include possible backend fields
  type AttachmentWithUrls = ContactAttachment & {
    convertedUrl?: string;
    downloadUrl?: string;
  };

  // Replace the existing handleOpenAttachment function (around line 590-610)
  const handleOpenAttachment = async (attachment: AttachmentWithUrls) => {
    try {
      // Attempting to open attachment

      // Use the attachment service to handle the download properly
      await contactService.attachments.openAttachment(attachment);
    } catch (error) {
      createApiError(
        ApiErrorCode.SERVER_ERROR,
        'Failed to open attachment. Please try again.',
        undefined,
        { originalError: error }
      );
    }
  };

  const getTypeClass = (type: string): string => {
    const lowerType = type.toLowerCase();

    if (lowerType.includes('mobile') || lowerType.includes('cell')) {
      return styles.typeMobile;
    } else if (lowerType.includes('home')) {
      return styles.typeHome;
    } else if (lowerType.includes('work') || lowerType.includes('office')) {
      return styles.typeWork;
    } else if (lowerType.includes('personal')) {
      return styles.typePersonal;
    } else if (lowerType.includes('business')) {
      return styles.typeBusiness;
    } else if (lowerType.includes('billing')) {
      return styles.typeBilling;
    } else if (lowerType.includes('custom')) {
      return styles.typeCustom;
    } else {
      return styles.typeOther;
    }
  };

  // Mobile/cell numbers get the handset-shaped glyph; everything else (home,
  // work, fax, unlabelled) keeps the generic call icon.
  const getPhoneIcon = (type?: string): string => {
    const t = (type || '').toLowerCase();
    return t.includes('mobile') || t.includes('cell') ? phonePortraitOutline : callOutline;
  };

  // Address tag drives the glyph: home/work get their building icons, billing
  // gets the card, shipping the box; anything else falls back to the map pin.
  const getAddressIcon = (type?: string): string => {
    const t = (type || '').toLowerCase();
    if (t.includes('home')) return homeOutline;
    if (t.includes('work') || t.includes('office') || t.includes('business')) return businessOutline;
    if (t.includes('billing')) return cardOutline;
    if (t.includes('ship')) return cubeOutline;
    if (t.includes('other') || t.includes('custom')) return locationOutline;
    return pinOutline;
  };

  // Rendered by *calling* renderContactInfo() (not <ContactInfo />) so its JSX
  // inlines into Contact's tree. As a nested React component it got a fresh
  // identity every Contact render, so React remounted the whole Info tab on
  // each keystroke — dropping input focus and closing pickers mid-edit.
  const renderContactInfo = () => {

    // If there's no contact, show a placeholder
    if (!contact) {
      return (
        <IonList className={styles.infoContainer}>
          <IonItem className={styles.contactInfoItem}>
            <IonLabel>
              <IonText color="medium">{tr('pages.bookingSystem.Contact.contactInformationNotAvailable')}</IonText>
            </IonLabel>
          </IonItem>
        </IonList>
      );
    }

    const startEditingSection = (section: string, seed: Partial<ContactInterface>) => {
      setContactFormData(prev => ({ ...prev, ...seed }));
      setEditingInfoSection(editingInfoSection === section ? null : section);
    };

    return (
      <IonList className={styles.infoContainer}>
        <div ref={infoPickerPortalRef} className={styles.pickerPortalRoot} />
        {/* Phone Numbers */}
        <IonCard className={styles.infoSection}>
          <IonItem lines="none" className={styles.sectionTitle}>
            <IonLabel>{tr('pages.bookingSystem.Contact.phoneNumbers')}</IonLabel>
            <IonButton slot="end" fill="clear" size="small" shape="round"
              className={`${styles.editButton} ${editingInfoSection === 'phones' ? styles.editButtonClose : ''}`}
              onClick={() => startEditingSection('phones', { phones: contact.phones })}>
              <IonIcon icon={editingInfoSection === 'phones' ? closeOutline : pencilOutline} />
            </IonButton>
          </IonItem>
          {editingInfoSection === 'phones' ? (
            <IonList className={styles.editForm}>
              {(contactFormData.phones ?? contact.phones ?? []).map((phone: ContactPhone, index: number) => (
                <PhoneEditRow
                  key={phone.id || `phone-${index}`}
                  phone={phone}
                  index={index}
                  portalRef={infoPickerPortalRef as React.RefObject<HTMLElement | null>}
                  onChange={(updated) => {
                    const list = [...(contactFormData.phones ?? contact.phones ?? [])];
                    list[index] = updated;
                    setContactFormData(prev => ({ ...prev, phones: list }));
                  }}
                  onDelete={() => {
                    const list = (contactFormData.phones ?? contact.phones ?? []).filter((_, i) => i !== index);
                    setContactFormData(prev => ({ ...prev, phones: list }));
                  }}
                />
              ))}
              <IonItem lines="none" className={styles.addRowItem} button detail={false} onClick={() => {
                const newPhone: ContactPhone = { id: `phone-${Date.now()}`, phone: '', formattedPhone: '', e164Phone: '', countryCode: '', tag: 'mobile', primary: false };
                const list = [...(contactFormData.phones ?? contact.phones ?? []), newPhone];
                setContactFormData(prev => ({ ...prev, phones: list }));
              }}>
                <IonIcon icon={addCircleOutline} slot="start" color="primary" />
                <IonLabel color="primary">{tr('pages.bookingSystem.Contact.addPhone')}</IonLabel>
              </IonItem>
              <IonRow className={styles.editActionsRow}>
                <IonButton size="small" onClick={handleSaveContactInfo} disabled={isSavingContactInfo}>
                  {isSavingContactInfo ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.save')}
                </IonButton>
                <IonButton size="small" fill="clear" onClick={handleCancelInfoEdit}>{tr('pages.bookingSystem.Contact.cancel')}</IonButton>
              </IonRow>
            </IonList>
          ) : (
            <IonList>
              {contact.phones?.[0] && (
                <IonItem lines="none" className={styles.navRow}>
                  <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={getPhoneIcon(contact.phones[0].tag || 'mobile')} />
                  </IonText>
                  <IonLabel>
                    <p className={styles.infoRowMeta}>
                      <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.primary')}</IonText>
                      <IonText className={`${styles.rowTag} ${getTypeClass(contact.phones[0].tag?.toLowerCase() || 'mobile')}`}>
                        {capitalizeFirstLetter(contact.phones[0].tag?.toLowerCase() || 'mobile')}
                      </IonText>
                    </p>
                    <h3>
                      {contact.phones[0].formattedPhone || contact.phones[0].e164Phone || contact.phones[0].phone}
                    </h3>
                  </IonLabel>
                </IonItem>
              )}
              {contact.phones?.slice(1).map((phone, index) => (
                <IonItem key={phone.id || `phone-${index}`} lines="none" className={styles.navRow}>
                  <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={getPhoneIcon(phone.tag || 'mobile')} />
                  </IonText>
                  <IonLabel>
                    <p className={styles.infoRowMeta}>
                      <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.additional')}</IonText>
                      <IonText className={`${styles.rowTag} ${getTypeClass(phone.tag || 'mobile')}`}>
                        {capitalizeFirstLetter(phone.tag || 'mobile')}
                      </IonText>
                    </p>
                    <h3>
                      {phone.formattedPhone || phone.e164Phone || phone.phone}
                    </h3>
                  </IonLabel>
                </IonItem>
              ))}
              {(!contact.phones || contact.phones.length === 0) && (
                <IonText color="medium" className={styles.emptyMessage}>{tr('pages.bookingSystem.Contact.noPhoneNumbers')}</IonText>
              )}
            </IonList>
          )}
        </IonCard>

        {/* Email Addresses */}
        <IonCard className={styles.infoSection}>
          <IonItem lines="none" className={styles.sectionTitle}>
            <IonLabel>{tr('pages.bookingSystem.Contact.emailAddresses')}</IonLabel>
            <IonButton slot="end" fill="clear" size="small" shape="round"
              className={`${styles.editButton} ${editingInfoSection === 'emails' ? styles.editButtonClose : ''}`}
              onClick={() => startEditingSection('emails', { emails: contact.emails })}>
              <IonIcon icon={editingInfoSection === 'emails' ? closeOutline : pencilOutline} />
            </IonButton>
          </IonItem>
          {editingInfoSection === 'emails' ? (
            <IonList className={styles.editForm}>
              {(contactFormData.emails ?? contact.emails ?? []).map((email: ContactEmail, index: number) => (
                <EmailEditRow
                  key={email.id || `email-${index}`}
                  email={email}
                  index={index}
                  portalRef={infoPickerPortalRef as React.RefObject<HTMLElement | null>}
                  onChange={(updated) => {
                    const list = [...(contactFormData.emails ?? contact.emails ?? [])];
                    list[index] = updated;
                    setContactFormData(prev => ({ ...prev, emails: list }));
                  }}
                  onDelete={() => {
                    const list = (contactFormData.emails ?? contact.emails ?? []).filter((_, i) => i !== index);
                    setContactFormData(prev => ({ ...prev, emails: list }));
                  }}
                />
              ))}
              <IonItem lines="none" className={styles.addRowItem} button detail={false} onClick={() => {
                const newEmail: ContactEmail = { id: `email-${Date.now()}`, email: '', tag: 'personal', primary: false };
                const list = [...(contactFormData.emails ?? contact.emails ?? []), newEmail];
                setContactFormData(prev => ({ ...prev, emails: list }));
              }}>
                <IonIcon icon={addCircleOutline} slot="start" color="primary" />
                <IonLabel color="primary">{tr('pages.bookingSystem.Contact.addEmail')}</IonLabel>
              </IonItem>
              <IonRow className={styles.editActionsRow}>
                <IonButton size="small" onClick={handleSaveContactInfo} disabled={isSavingContactInfo}>
                  {isSavingContactInfo ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.save')}
                </IonButton>
                <IonButton size="small" fill="clear" onClick={handleCancelInfoEdit}>{tr('pages.bookingSystem.Contact.cancel')}</IonButton>
              </IonRow>
            </IonList>
          ) : (
            <IonList>
              {contact.emails?.[0] && (
                <IonItem lines="none" className={styles.navRow}>
                  <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                    <IonIcon icon={mailOutline} />
                  </IonText>
                  <IonLabel>
                    <p className={styles.infoRowMeta}>
                      <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.primary')}</IonText>
                      <IonText className={`${styles.rowTag} ${getTypeClass(contact.emails[0].tag?.toLowerCase() || 'personal')}`}>
                        {capitalizeFirstLetter(contact.emails[0].tag?.toLowerCase() || 'personal')}
                      </IonText>
                    </p>
                    <h3>{contact.emails[0].email}</h3>
                  </IonLabel>
                </IonItem>
              )}
              {contact.emails?.slice(1).map((email, index) => (
                <IonItem key={email.id || `email-${index}`} lines="none" className={styles.navRow}>
                  <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                    <IonIcon icon={mailOutline} />
                  </IonText>
                  <IonLabel>
                    <p className={styles.infoRowMeta}>
                      <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.additional')}</IonText>
                      <IonText className={`${styles.rowTag} ${getTypeClass(email.tag || 'personal')}`}>
                        {capitalizeFirstLetter(email.tag || 'personal')}
                      </IonText>
                    </p>
                    <h3>{email.email}</h3>
                  </IonLabel>
                </IonItem>
              ))}
              {(!contact.emails || contact.emails.length === 0) && (
                <IonText color="medium" className={styles.emptyMessage}>{tr('pages.bookingSystem.Contact.noEmailAddresses')}</IonText>
              )}
            </IonList>
          )}
        </IonCard>

        {/* Addresses */}
        {(contact.addresses && contact.addresses.length > 0 || editingInfoSection === 'addresses') && (
          <IonCard className={styles.infoSection}>
            <IonItem lines="none" className={styles.sectionTitle}>
              <IonLabel>{tr('pages.bookingSystem.Contact.addresses')}</IonLabel>
              <IonButton slot="end" fill="clear" size="small" shape="round"
                className={`${styles.editButton} ${editingInfoSection === 'addresses' ? styles.editButtonClose : ''}`}
                onClick={() => startEditingSection('addresses', { addresses: contact.addresses })}>
                <IonIcon icon={editingInfoSection === 'addresses' ? closeOutline : pencilOutline} />
              </IonButton>
            </IonItem>
            {editingInfoSection === 'addresses' ? (
              <IonList className={styles.editForm}>
                {(contactFormData.addresses ?? contact.addresses ?? []).map((address: ContactAddress, index: number) => (
                  <AddressEditRow
                    key={address.id || `address-${index}`}
                    address={address}
                    index={index}
                    portalRef={infoPickerPortalRef as React.RefObject<HTMLElement | null>}
                    onChange={(updated) => {
                      const list = [...(contactFormData.addresses ?? contact.addresses ?? [])];
                      list[index] = updated;
                      setContactFormData(prev => ({ ...prev, addresses: list }));
                    }}
                    onDelete={() => {
                      const list = (contactFormData.addresses ?? contact.addresses ?? []).filter((_, i) => i !== index);
                      setContactFormData(prev => ({ ...prev, addresses: list }));
                    }}
                  />
                ))}
                <IonItem lines="none" className={styles.addRowItem} button detail={false} onClick={() => {
                  const newAddress: ContactAddress = {
                    id: `address-${Date.now()}`,
                    address: { addressLine1: '', addressLine2: '', city: '', subdivision: '', postalCode: '', country: '' },
                    tag: 'home',
                    primary: false,
                  };
                  const list = [...(contactFormData.addresses ?? contact.addresses ?? []), newAddress];
                  setContactFormData(prev => ({ ...prev, addresses: list }));
                }}>
                  <IonIcon icon={addCircleOutline} slot="start" color="primary" />
                  <IonLabel color="primary">{tr('pages.bookingSystem.Contact.addAddress')}</IonLabel>
                </IonItem>
                <IonRow className={styles.editActionsRow}>
                  <IonButton size="small" onClick={handleSaveContactInfo} disabled={isSavingContactInfo}>
                    {isSavingContactInfo ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.save')}
                  </IonButton>
                  <IonButton size="small" fill="clear" onClick={handleCancelInfoEdit}>{tr('pages.bookingSystem.Contact.cancel')}</IonButton>
                </IonRow>
              </IonList>
            ) : (
              <IonList>
                {contact.addresses?.[0] && (
                  <IonItem
                    lines="none"
                    className={styles.navRow}
                    button
                    detail={false}
                    onClick={() => openMaps(`${contact.addresses[0].address.addressLine1 || ''}, ${contact.addresses[0].address.city || ''}, ${contact.addresses[0].address.subdivision || ''} ${contact.addresses[0].address.postalCode || ''}, ${contact.addresses[0].address.country || ''}`)}
                  >
                    <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                      <IonIcon icon={getAddressIcon(contact.addresses[0].tag || 'home')} />
                    </IonText>
                    <IonLabel>
                      <p className={styles.infoRowMeta}>
                        <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.primary')}</IonText>
                        <IonText className={`${styles.rowTag} ${getTypeClass(contact.addresses[0].tag || 'home')}`}>
                          {capitalizeFirstLetter(contact.addresses[0].tag || 'home')}
                        </IonText>
                      </p>
                      <h3>
                        {contact.addresses[0].address.addressLine1}
                      </h3>
                      <p className={styles.infoRowSub}>
                        {[contact.addresses[0].address.addressLine2, contact.addresses[0].address.city,(contact.addresses[0].address as { state?: string }).state || contact.addresses[0].address.subdivision, contact.addresses[0].address.postalCode, contact.addresses[0].address.country].filter(Boolean).join(', ')}
                      </p>
                    </IonLabel>
                  </IonItem>
                )}
                {contact.addresses?.slice(1).map((address, index) => (
                  <IonItem
                    key={address.id || `address-${index}`}
                    lines="none"
                    className={styles.navRow}
                    button
                    detail={false}
                    onClick={() => openMaps(`${address.address.addressLine1 || ''}, ${address.address.city || ''}, ${address.address.subdivision || ''} ${address.address.postalCode || ''}, ${address.address.country || ''}`)}
                  >
                    <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                      <IonIcon icon={getAddressIcon(address.tag || 'home')} />
                    </IonText>
                    <IonLabel>
                      <p className={styles.infoRowMeta}>
                        <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.additional')}</IonText>
                        <IonText className={`${styles.rowTag} ${getTypeClass(address.tag || 'home')}`}>
                          {capitalizeFirstLetter(address.tag || 'home')}
                        </IonText>
                      </p>
                      <h3>
                        {address.address.addressLine1}
                      </h3>
                      <p className={styles.infoRowSub}>
                        {[address.address.addressLine2, address.address.city,(address.address as { state?: string }).state || address.address.subdivision, address.address.postalCode, address.address.country].filter(Boolean).join(', ')}
                      </p>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonCard>
        )}

        {/* Company */}
        {contact.company && (
          <IonCard className={styles.infoSection}>
            <IonItem lines="none" className={styles.sectionTitle}>
              <IonIcon icon={businessOutline} slot="start" className={styles.sectionTitleIcon} />
              <IonLabel>{tr('pages.bookingSystem.Contact.company')}</IonLabel>
              <IonButton slot="end" fill="clear" size="small" shape="round" className={styles.editButton}
                onClick={() => editingInfoSection === 'company' ? handleRemoveInfoField('company') : startEditingSection('company', { company: contact.company || '' })}>
                <IonIcon icon={editingInfoSection === 'company' ? trashOutline : pencilOutline} />
              </IonButton>
            </IonItem>
            {editingInfoSection === 'company' ? (
              <IonList className={styles.editForm}>
                <IonItem lines="none">
                  <IonInput value={contactFormData.company ?? contact.company ?? ''} placeholder={tr('pages.bookingSystem.Contact.companyName')}
                    onIonInput={(e) => setContactFormData(prev => ({ ...prev, company: e.detail.value as string }))} />
                </IonItem>
                <IonRow className={styles.editActionsRow}>
                  <IonButton size="small" onClick={handleSaveContactInfo} disabled={isSavingContactInfo}>
                    {isSavingContactInfo ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.save')}
                  </IonButton>
                  <IonButton size="small" fill="clear" onClick={handleCancelInfoEdit}>{tr('pages.bookingSystem.Contact.cancel')}</IonButton>
                </IonRow>
              </IonList>
            ) : (
              <IonItem lines="none" className={styles.navRow}>
                <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                  <IonIcon icon={businessOutline} />
                </IonText>
                <IonLabel><h3>{contact.company}</h3></IonLabel>
              </IonItem>
            )}

          </IonCard>
        )}

        {/* Position */}
        {contact.jobTitle && (
          <IonCard className={styles.infoSection}>
            <IonItem lines="none" className={styles.sectionTitle}>
              <IonIcon icon={briefcaseOutline} slot="start" className={styles.sectionTitleIcon} />
              <IonLabel>{tr('pages.bookingSystem.Contact.position')}</IonLabel>
              <IonButton slot="end" fill="clear" size="small" shape="round" className={styles.editButton}
                onClick={() => editingInfoSection === 'position' ? handleRemoveInfoField('position') : startEditingSection('position', { jobTitle: contact.jobTitle || '' })}>
                <IonIcon icon={editingInfoSection === 'position' ? trashOutline : pencilOutline} />
              </IonButton>
            </IonItem>
            {editingInfoSection === 'position' ? (
              <IonList className={styles.editForm}>
                <IonItem lines="none">
                  <IonInput value={contactFormData.jobTitle ?? contact.jobTitle ?? ''} placeholder={tr('pages.bookingSystem.Contact.jobTitleOrPosition')}
                    onIonInput={(e) => setContactFormData(prev => ({ ...prev, jobTitle: e.detail.value as string }))} />
                </IonItem>
                <IonRow className={styles.editActionsRow}>
                  <IonButton size="small" onClick={handleSaveContactInfo} disabled={isSavingContactInfo}>
                    {isSavingContactInfo ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.save')}
                  </IonButton>
                  <IonButton size="small" fill="clear" onClick={handleCancelInfoEdit}>{tr('pages.bookingSystem.Contact.cancel')}</IonButton>
                </IonRow>
              </IonList>
            ) : (
              <IonItem lines="none" className={styles.navRow}>
                <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                  <IonIcon icon={briefcaseOutline} />
                </IonText>
                <IonLabel><h3>{contact.jobTitle}</h3></IonLabel>
              </IonItem>
            )}
          </IonCard>
        )}

        {/* Display Name — picks which existing field (Company or Full Name) feeds Zoho's
            Display Name; a direct Firestore write that bypasses Wix, so it saves through
            its own handler rather than the shared handleSaveContactInfo/updateContact path. */}
        {contact.displayNameSource && (
          <IonCard className={styles.infoSection}>
            <IonItem lines="none" className={styles.sectionTitle}>
              <IonIcon icon={pricetagOutline} slot="start" className={styles.sectionTitleIcon} />
              <IonLabel>{tr('pages.bookingSystem.Contact.displayName')}</IonLabel>
              <IonButton slot="end" fill="clear" size="small" shape="round" className={styles.editButton}
                onClick={() => editingInfoSection === 'displayNameSource' ? handleRemoveDisplayNameSource() : startEditingSection('displayNameSource', { displayNameSource: contact.displayNameSource })}>
                <IonIcon icon={editingInfoSection === 'displayNameSource' ? trashOutline : pencilOutline} />
              </IonButton>
            </IonItem>
            {editingInfoSection === 'displayNameSource' ? (
              <IonList className={styles.editForm}>
                <IonRow className={styles.choicePillRow}>
                  <IonButton
                    size="small"
                    fill={(contactFormData.displayNameSource ?? contact.displayNameSource) === 'company' ? 'solid' : 'outline'}
                    onClick={() => setContactFormData(prev => ({ ...prev, displayNameSource: 'company' }))}
                  >{tr('pages.bookingSystem.Contact.companyName2')}
                  </IonButton>
                  <IonButton
                    size="small"
                    fill={(contactFormData.displayNameSource ?? contact.displayNameSource) === 'name' ? 'solid' : 'outline'}
                    onClick={() => setContactFormData(prev => ({ ...prev, displayNameSource: 'name' }))}
                  >{tr('pages.bookingSystem.Contact.fullName')}
                  </IonButton>
                </IonRow>
                <IonRow className={styles.editActionsRow}>
                  <IonButton size="small" onClick={handleSaveContactInfo} disabled={isSavingContactInfo}>
                    {isSavingContactInfo ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.save')}
                  </IonButton>
                  <IonButton size="small" fill="clear" onClick={handleCancelInfoEdit}>{tr('pages.bookingSystem.Contact.cancel')}</IonButton>
                </IonRow>
              </IonList>
            ) : (
              <IonList>
                <IonItem lines="none" className={styles.navRow}>
                  <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                    <IonIcon icon={pricetagOutline} />
                  </IonText>
                  <IonLabel>
                    <h3>
                      {contact.displayNameSource === 'company' ? tr('pages.bookingSystem.Contact.companyName3') : contact.displayNameSource === 'name' ? tr('pages.bookingSystem.Contact.fullName') : ''}
                    </h3>
                  </IonLabel>
                </IonItem>
              </IonList>
            )}
          </IonCard>
        )}

        {/* Birthdate */}
        {contact.birthdate && (
          <IonCard className={styles.infoSection}>
            <IonItem lines="none" className={styles.sectionTitle}>
              <IonIcon icon={calendarOutline} slot="start" className={styles.sectionTitleIcon} />
              <IonLabel>{tr('pages.bookingSystem.Contact.birthdate')}</IonLabel>
              <IonButton slot="end" fill="clear" size="small" shape="round" className={styles.editButton}
                onClick={() => editingInfoSection === 'birthdate' ? handleRemoveInfoField('birthdate') : startEditingSection('birthdate', { birthdate: contact.birthdate || '' })}>
                <IonIcon icon={editingInfoSection === 'birthdate' ? trashOutline : pencilOutline} />
              </IonButton>
            </IonItem>
            {editingInfoSection === 'birthdate' ? (
              <IonList className={styles.editForm}>
                <IonRow className={styles.birthdateEditRow}>
                  <IonButton
                    ref={infoBirthdateFieldRef}
                    fill="clear"
                    className={`${styles.birthdateTrigger} ${infoBirthdateOpen ? styles.birthdateTriggerOpen : ''}`}
                    onClick={() => setInfoBirthdateOpen(o => !o)}
                  >
                    <IonText
                      className={styles.birthdateTriggerText}
                      style={{
                        color: (contactFormData.birthdate ?? contact.birthdate)
                          ? 'var(--glass-card-text, var(--ion-text-color))'
                          : 'var(--glass-card-text-secondary, var(--ion-color-medium))',
                      }}
                    >
                      {(contactFormData.birthdate ?? contact.birthdate)
                        ? new Date(String(contactFormData.birthdate ?? contact.birthdate) + 'T12:00:00').toLocaleDateString(intlLocale(), { month: 'long', day: 'numeric', year: 'numeric' })
                        : tr('pages.bookingSystem.Contact.selectBirthdate')}
                    </IonText>
                    <IonIcon icon={calendarOutline} className={styles.birthdateTriggerCaret} />
                  </IonButton>
                </IonRow>
                <PortalPickerDropdown
                  isOpen={infoBirthdateOpen}
                  fieldRef={infoBirthdateFieldRef as React.RefObject<HTMLElement | null>}
                  portalRef={infoPickerPortalRef as React.RefObject<HTMLElement | null>}
                  close={() => setInfoBirthdateOpen(false)}
                  align="left"
                  className={styles.birthdateDropdown}
                >
                  <MiniCalendarPicker
                    value={(contactFormData.birthdate ?? contact.birthdate) ? new Date(String(contactFormData.birthdate ?? contact.birthdate) + 'T12:00:00') : null}
                    onSelect={(d) => {
                      const y = d.getFullYear();
                      const m = String(d.getMonth() + 1).padStart(2, '0');
                      const day = String(d.getDate()).padStart(2, '0');
                      setContactFormData(prev => ({ ...prev, birthdate: `${y}-${m}-${day}` }));
                      setInfoBirthdateOpen(false);
                    }}
                  />
                </PortalPickerDropdown>
                <IonRow className={styles.editActionsRow}>
                  <IonButton size="small" onClick={handleSaveContactInfo} disabled={isSavingContactInfo}>
                    {isSavingContactInfo ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.save')}
                  </IonButton>
                  <IonButton size="small" fill="clear" onClick={handleCancelInfoEdit}>{tr('pages.bookingSystem.Contact.cancel')}</IonButton>
                </IonRow>
              </IonList>
            ) : (
              <IonItem lines="none" className={styles.navRow}>
                <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeWarning}`}>
                  <IonIcon icon={calendarOutline} />
                </IonText>
                <IonLabel>
                  <h3>
                    {contact.birthdate
                      ? new Date(String(contact.birthdate) + 'T12:00:00').toLocaleDateString(intlLocale(), { month: 'long', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </h3>
                </IonLabel>
              </IonItem>
            )}
          </IonCard>
        )}

        {/* Custom Fields */}
        {contact.customFields && contact.customFields.length > 0 && (
          <IonCard className={styles.infoSection}>
            <IonItem lines="none" className={styles.sectionTitle}>
              <IonIcon icon={informationCircleOutline} slot="start" className={styles.sectionTitleIcon} />
              <IonLabel>{tr('pages.bookingSystem.Contact.customFields')}</IonLabel>
              <IonButton slot="end" fill="clear" size="small" shape="round"
                className={`${styles.editButton} ${editingInfoSection === 'customFields' ? styles.editButtonClose : ''}`}
                onClick={() => startEditingSection('customFields', { customFields: contact.customFields })}>
                <IonIcon icon={editingInfoSection === 'customFields' ? closeOutline : pencilOutline} />
              </IonButton>
            </IonItem>
            {editingInfoSection === 'customFields' ? (
              <IonList className={styles.editForm}>
                {(contact.customFields || []).map((field: CustomField, index: number) => (
                  <IonItem key={field.id || `field-${index}`} lines="none" className={styles.editEntry}>
                    <IonInput value={field.label || ''} placeholder={tr('pages.bookingSystem.Contact.fieldLabel')} style={{ marginBottom: 4 }}
                      onIonInput={(e) => {
                        const updated = [...(contact.customFields || [])];
                        updated[index] = { ...field, label: e.detail.value as string };
                        setContact({ ...contact, customFields: updated });
                        setContactFormData(prev => ({ ...prev, customFields: updated }));
                      }} />
                    <IonInput value={field.value || ''} placeholder={tr('pages.bookingSystem.Contact.fieldValue')}
                      onIonInput={(e) => {
                        const updated = [...(contact.customFields || [])];
                        updated[index] = { ...field, value: e.detail.value as string };
                        setContact({ ...contact, customFields: updated });
                        setContactFormData(prev => ({ ...prev, customFields: updated }));
                      }} />
                    <IonButton fill="clear" color="danger" slot="end" onClick={() => {
                      const updated = (contact.customFields || []).filter((_, i) => i !== index);
                      setContact({ ...contact, customFields: updated });
                      setContactFormData(prev => ({ ...prev, customFields: updated }));
                    }}>
                      <IonIcon icon={trashOutline} slot="icon-only" />
                    </IonButton>
                  </IonItem>
                ))}
                <IonItem lines="none" className={styles.addRowItem} button detail={false} onClick={() => {
                  const newField: CustomField = { id: `custom-${Date.now()}`, label: '', value: '', type: 'text', fieldKey: '' };
                  const updated = [...(contact.customFields || []), newField];
                  setContact({ ...contact, customFields: updated });
                  setContactFormData(prev => ({ ...prev, customFields: updated }));
                }}>
                  <IonIcon icon={addCircleOutline} slot="start" color="primary" />
                  <IonLabel color="primary">{tr('pages.bookingSystem.Contact.addCustomField')}</IonLabel>
                </IonItem>
                <IonRow className={styles.editActionsRow}>
                  <IonButton size="small" onClick={handleSaveContactInfo} disabled={isSavingContactInfo}>
                    {isSavingContactInfo ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.save')}
                  </IonButton>
                  <IonButton size="small" fill="clear" onClick={handleCancelInfoEdit}>{tr('pages.bookingSystem.Contact.cancel')}</IonButton>
                </IonRow>
              </IonList>
            ) : (
              <IonList>
                {(contact.customFields || []).map((field, index) => (
                  <IonItem key={field.id || `field-${index}`} lines="none" className={styles.navRow}>
                    <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={informationCircleOutline} />
                    </IonText>
                    <IonLabel>
                      <p className={styles.infoRowMeta}>
                        <IonText className={styles.rowLabel}>{field.label}</IonText>
                      </p>
                      <h3>{field.value || '—'}</h3>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonCard>
        )}

        {/* Labels */}
        <IonCard className={styles.infoSection}>
          <IonItem lines="none" className={styles.sectionTitle}>
            <IonLabel>{tr('pages.bookingSystem.Contact.labels')}</IonLabel>
            <IonButton
              slot="end"
              fill="clear"
              size="small"
              shape="round"
              className={styles.headerActionButton}
              onClick={handleAssignLabels}
              type="button"
            >
              <IonIcon icon={enterOutline} />
            </IonButton>
          </IonItem>
          {(!contact.labels || contact.labels.length === 0) ? (
            <IonText color="medium" className={styles.emptyMessage}>{tr('pages.bookingSystem.Contact.noLabelsAddedToThisContact')}
            </IonText>
          ) : (
            <IonItem className={`${styles.contactInfoItem} ${styles.noBorder}`}>
              <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                <IonIcon icon={pricetagOutline} />
              </IonText>
              <IonCard className={styles.labelContainer}>
                <IonList>
                  {(contact.labels || []).map((labelId: string, index: number) => {
                    const labelInfo = availableLabels.find(l => l.id === labelId || l.key === labelId || l._id === labelId);
                    const displayName = labelInfo?.name || (() => {
                      const keyPart = labelId.split('.').pop() || '';
                      const fallbackName = keyPart.replace(/-/g, ' ');
                      return fallbackName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                    })();
                    const color = labelInfo?.color || '#3880ff';
                    const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
                    const bgOpacity = isDarkMode ? '20' : '10';
                    const bgColor = `${color}${bgOpacity}`;
                    return (
                      <IonChip key={labelId || `label-${index}`} className={styles.contactLabel} style={{ backgroundColor: bgColor, color }}>
                        {displayName}
                      </IonChip>
                    );
                  })}
                </IonList>
              </IonCard>
            </IonItem>
          )}
        </IonCard>

        {/* Attachments */}
        <IonCard className={styles.infoSection}>
          <IonItem lines="none" className={styles.sectionTitle}>
            <IonLabel>{tr('pages.bookingSystem.Contact.attachments')}</IonLabel>
            <IonButton
              slot="end"
              fill="clear"
              size="small"
              shape="round"
              className={styles.headerActionButton}
              onClick={handleAddAttachment}
            >
              <IonIcon icon={attach} />
            </IonButton>
          </IonItem>
          {contact?.attachments && contact.attachments.length > 0 ? (
            <IonList>
              {(contact.attachments || []).map((attachment: ContactAttachment, index: number) => (
                <IonItem
                  key={attachment.id || `attachment-${index}`}
                  className={styles.navRow}
                  lines="none"
                  button
                  detail={false}
                  onClick={() => handleOpenAttachment(attachment)}
                >
                  <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={getAttachmentIcon(attachment.type)} />
                  </IonText>
                  <IonLabel>
                    <p className={styles.infoRowMeta}>
                      <IonText className={styles.rowLabel}>
                        {formatFileSize(attachment.size)} • {formatDateSafe(attachment.date)}
                      </IonText>
                    </p>
                    <h3>{attachment.name}</h3>
                  </IonLabel>
                  <IonButton fill="clear" slot="end" onClick={(e) => handleDeleteAttachment(e, attachment.id)}>
                    <IonIcon icon={trashOutline} color="danger" />
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          ) : (
            <IonText color="medium" className={styles.emptyMessage}>{tr('pages.bookingSystem.Contact.noAttachmentsAddedToThisContact')}
            </IonText>
          )}
        </IonCard>

        {/* External System IDs */}
        <IonCard className={styles.infoSection}>
          <IonItem
            lines="none"
            className={styles.sectionTitle}
            onClick={() => setSystemIdsExpanded(v => !v)}
            style={{ cursor: 'pointer' }}
          >
            <IonLabel>{tr('pages.bookingSystem.Contact.systemIds')}</IonLabel>
            <IonIcon
              slot="end"
              icon={chevronDownOutline}
              className={`${styles.collapseChevron} ${systemIdsExpanded ? styles.collapseChevronOpen : ''}`}
            />
          </IonItem>
          {systemIdsExpanded && (
            <IonList>
              <IonItem lines="none" className={styles.navRow}>
                <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                  <IonIcon icon={informationCircleOutline} />
                </IonText>
                <IonLabel>
                  <p className={styles.infoRowMeta}>
                    <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.bookingsIdWix')}</IonText>
                  </p>
                  <h3 className={styles.idValue}>{id}</h3>
                </IonLabel>
                <IonButton fill="clear" slot="end" onClick={() => { try { navigator.clipboard.writeText(id); } catch { } }}>
                  <IonIcon icon={copyOutline} />
                </IonButton>
              </IonItem>
              <IonItem lines="none" className={styles.navRow}>
                <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                  <IonIcon icon={informationCircleOutline} />
                </IonText>
                <IonLabel>
                  <p className={styles.infoRowMeta}>
                    <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.paymentsIdHelcim')}</IonText>
                  </p>
                  <h3 className={styles.idValue}>{externalIds.helcimCustomerCode || '—'}</h3>
                </IonLabel>
                {externalIds.helcimCustomerCode && (
                  <IonButton fill="clear" slot="end" onClick={() => { try { navigator.clipboard.writeText(externalIds.helcimCustomerCode); } catch { } }}>
                    <IonIcon icon={copyOutline} />
                  </IonButton>
                )}
              </IonItem>
              <IonItem lines="none" className={styles.navRow}>
                <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                  <IonIcon icon={informationCircleOutline} />
                </IonText>
                <IonLabel>
                  <p className={styles.infoRowMeta}>
                    <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.invoiceIdZoho')}</IonText>
                  </p>
                  <h3 className={styles.idValue}>{externalIds.zohoContactId || '—'}</h3>
                </IonLabel>
                {externalIds.zohoContactId && (
                  <IonButton fill="clear" slot="end" onClick={() => { try { navigator.clipboard.writeText(externalIds.zohoContactId); } catch { } }}>
                    <IonIcon icon={copyOutline} />
                  </IonButton>
                )}
              </IonItem>
            </IonList>
          )}
        </IonCard>
      </IonList>
    );
  };

  useEffect(() => {
    const inner = innerScrollRef.current;
    const outer = contentRef.current;
    if (!inner || !outer) return;

    let startY = 0;
    let startOuter = 0;
    let startInner = 0;

    // Where the tabs reach their sticky spot: the card's height PLUS its margins. The wrapper's own height leaves out
    // the card's bottom margin (it collapses through the wrapper), so touch scrolling stopped 8px short and left the
    // bottom edge of the card showing above the tabs; the wheel path overshoots into the right spot, so it looked fine.
    const getStickyThreshold = () => {
      const card = titleCardAnimationRef.current;
      if (!card) return titleCardRef.current?.offsetHeight ?? 0;
      const cs = getComputedStyle(card);
      return card.offsetHeight + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
    };

    const applyDelta = (totalDelta: number) => {
      const threshold = getStickyThreshold();
      if (totalDelta > 0) {
        const outerHeadroom = threshold - startOuter;
        if (totalDelta <= outerHeadroom) {
          outer.scrollTop = startOuter + totalDelta;
        } else {
          outer.scrollTop = threshold;
          inner.scrollTop = startInner + (totalDelta - outerHeadroom);
        }
      } else {
        const innerUnwind = startInner + totalDelta;
        if (innerUnwind >= 0) {
          inner.scrollTop = innerUnwind;
        } else {
          inner.scrollTop = 0;
          outer.scrollTop = Math.max(0, startOuter + innerUnwind);
        }
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return; // wheel handles mouse
      inner.setPointerCapture(e.pointerId);
      startY = e.clientY;
      startOuter = outer.scrollTop;
      startInner = inner.scrollTop;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!inner.hasPointerCapture(e.pointerId)) return;
      e.preventDefault();
      applyDelta(startY - e.clientY);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (inner.hasPointerCapture(e.pointerId)) {
        inner.releasePointerCapture(e.pointerId);
      }
    };

    const onWheel = (e: WheelEvent) => {
      const stickyThreshold = getStickyThreshold();
      if (e.deltaY > 0 && outer.scrollTop < stickyThreshold) {
        e.preventDefault();
        outer.scrollTop += e.deltaY;
        inner.scrollTop = 0;
        return;
      }
      if (e.deltaY < 0 && inner.scrollTop === 0) {
        e.preventDefault();
        outer.scrollTop += e.deltaY;
        return;
      }
    };

    inner.addEventListener('pointerdown', onPointerDown);
    inner.addEventListener('pointermove', onPointerMove, { passive: false });
    inner.addEventListener('pointerup', onPointerUp);
    inner.addEventListener('pointercancel', onPointerUp);
    inner.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      inner.removeEventListener('pointerdown', onPointerDown);
      inner.removeEventListener('pointermove', onPointerMove);
      inner.removeEventListener('pointerup', onPointerUp);
      inner.removeEventListener('pointercancel', onPointerUp);
      inner.removeEventListener('wheel', onWheel);
    };
  }, []);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const applyHistoryProgress = useCallback((progress: number, _smooth: boolean) => {
    historyHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (historyTitleCardRef.current) {
      historyTitleCardRef.current.style.setProperty('--content-opacity', String(1 - progress));
      historyTitleCardRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  useEffect(() => {
    const card = titleCardAnimationRef.current;
    if (!card) return;
    const measure = () => {
      const cs = getComputedStyle(card);
      const distance = card.offsetHeight + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
      if (distance > 0) setTitleCollapseDistance(distance);
    };
    const observer = new ResizeObserver(measure); // fires once on observe, then whenever the card's height changes
    observer.observe(card);
    return () => observer.disconnect();
  }, [contact]);

  const handleScroll = useScrollSnap(contentRef, titleCollapseDistance, applyProgress);
  const handleHistoryScroll = useScrollSnap(historyScrollRef, 66, applyHistoryProgress);

  const PageWrapper = embedded ? React.Fragment : IonPage;
  const pageWrapperProps = embedded ? {} : { className: styles.page };
  return (
    <PageWrapper {...pageWrapperProps}>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={contact ? contactDisplayName : tr('pages.bookingSystem.Contact.contact')}
        toolbarClassName={styles.customToolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        headerAvatarClassName={styles.headerAvatar}
        avatarUrl={contact?.avatar ?? ''}
        avatarInitials={contact ? contactDisplayName : undefined}
        startButtons={
          embedded ? (
            <IonButton shape="round" onClick={onClose} aria-label={tr('pages.bookingSystem.Contact.back')}>
              <IonIcon icon={chevronBack} />
            </IonButton>
          ) : (
            <IonBackButton
              type="button"
              icon={chevronBack}
              defaultHref="/tabs/contacts"
            />
          )
        }
        endButtons={
          <IonButton ref={menuBtnRef} className={styles.circleButtonEllipsis} onClick={toggleMenu} disabled={isLoading || !contact}>
            <IonIcon slot="icon-only" icon={ellipsisVertical} />
          </IonButton>
        }
      >
        <IonContent scrollY={false} className={styles.content}>
          <PositionedRefresher
            titleRef={titleCardRef}
            contentRef={contentRef}
            offset={-12}
            anchor="bottom"
            id="contact-refresher"
            pullMin={120}
            pullMax={200}
            pullFactor={0.6}
            disabled={refresherDisabled}
            onIonRefresh={handleRefresh}
            style={{
              '--ion-color-step-250': 'var(--ion-background-color)',
              '--ion-color-step-200': 'var(--ion-background-color)',
              '--ion-background-color': 'transparent',
              zIndex: 10,
            } as React.CSSProperties}
          >
            <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
          </PositionedRefresher>

          <IonGrid
            ref={contentRef}
            className={styles.scrollContent}
            onScroll={(e) => {
              const top = (e.target as HTMLElement).scrollTop || 0;
              const atTop = top <= 5;
              if (atTop !== wasAtTop.current) {
                wasAtTop.current = atTop;
                setRefresherDisabled(!atTop);
              }
              handleScroll(top);
            }}
          >
            <div ref={titleCardRef} className={styles.titleCardWrapper}>
              {/* Left-aligned, avatar-beside-name hero, matching desktop's compact
                  contact-card header — not the centered-title layout TitleCard's own
                  built-in title/avatar renders (which hardcodes centered text-align),
                  so the avatar/name/links here are all custom children instead. */}
              <TitleCard
                ref={titleCardAnimationRef}
                title=""
                cardClassName={styles.avatarItem}
              >
                {contact ? (
                  <>
                    <IonItem lines="none" className={styles.heroItem}>
                      <IonAvatar slot="start" className={styles.largeAvatar} style={{ marginInlineEnd: '24px' }}>
                        <img
                          src={proxyImage(contact.avatar ?? '') || getDefaultAvatar(contactDisplayName)}
                          alt={contactDisplayName}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = getDefaultAvatar(contactDisplayName);
                          }}
                        />
                      </IonAvatar>
                      <IonLabel className={styles.heroNameCol}>
                        <h1 className={styles.contactName}>{contactDisplayName}</h1>
                        {(() => {
                          if (contact.memberInfo && typeof contact.memberInfo === 'object') {
                            const memberStatus = contact.memberInfo.status;
                            if (typeof memberStatus === 'string') {
                              switch (memberStatus.toUpperCase()) {
                                case 'APPROVED':
                                case 'ACTIVE':
                                  return (
                                    <IonChip className={`${styles.memberChip} ${styles.memberActive}`}>
                                      <IonLabel>{tr('pages.bookingSystem.Contact.member')}</IonLabel>
                                    </IonChip>
                                  );
                                case 'PENDING':
                                  return (
                                    <IonChip className={`${styles.memberChip} ${styles.memberPending}`}>
                                      <IonLabel>{tr('pages.bookingSystem.Contact.pendingMember')}</IonLabel>
                                    </IonChip>
                                  );
                                case 'BLOCKED':
                                  return (
                                    <IonChip className={`${styles.memberChip} ${styles.memberBlocked}`}>
                                      <IonLabel>{tr('pages.bookingSystem.Contact.blockedMember')}</IonLabel>
                                    </IonChip>
                                  );
                              }
                            }
                          }
                          return null;
                        })()}

                        {contact.emails?.[0] && (
                          <p className={styles.heroLinkLabel} onClick={() => window.open(`mailto:${contact.emails[0].email}`, '_system')}>
                            <IonIcon icon={mailOutline} className={styles.heroLinkIcon} />
                            {contact.emails[0].email}
                          </p>
                        )}
                      </IonLabel>
                    </IonItem>

                    <IonButtons className={styles.heroQuickActionsRow}>
                      {contact.phones?.[0] && (
                        <IonButton fill="clear" size="small" className={styles.heroQuickActionBtn}
                          onClick={() => { void placePhoneCall(contact.phones[0].phone); }}>
                          <IonIcon icon={callOutline} slot="start" />{tr('pages.bookingSystem.Contact.call')}
                        </IonButton>
                      )}
                      <IonButton fill="clear" size="small" className={styles.heroQuickActionBtn}
                        onClick={() => ionRouter.push('/tabs/Inbox', 'forward', 'push')}>
                        <IonIcon icon={chatbubbleOutline} slot="start" />{tr('pages.bookingSystem.Contact.message')}
                      </IonButton>
                    </IonButtons>
                  </>
                ) : (
                  <IonItem lines="none" className={styles.heroItem}>
                    <IonSkeletonText slot="start" animated className={styles.largeAvatar} style={{ borderRadius: '50%' }} />
                    <IonLabel className={styles.heroNameCol}>
                      <IonSkeletonText animated style={{ width: '70%', height: '20px' }} />
                      <IonSkeletonText animated style={{ width: '60%', height: '14px', marginTop: '8px' }} />
                    </IonLabel>
                  </IonItem>
                )}
              </TitleCard>
            </div>

            <IonToolbar className={styles.segmentToolbar}>
              <IonSegment value={selectedSegment} onIonChange={handleSegmentChange}>
                <IonSegmentButton className={styles.segmentButton} value="details">
                  <IonLabel>{tr('pages.bookingSystem.Contact.details')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton className={styles.segmentButton} value="info">
                  <IonLabel>{tr('pages.bookingSystem.Contact.info')}</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonToolbar>

            <div ref={innerScrollRef} className={styles.innerScrollContainer}>
              {/* Segment content — gated on `contact` only (hero identity data),
                  not the broader `isLoading`/`isRefreshing` flags, which stay true
                  until bookings/subscriptions/stats/etc. all resolve. ContactDetails
                  itself handles its own internal skeleton for those data sections
                  via `parentIsLoadingDetailsData`, so its static Circle Buttons Row
                  can render immediately once `contact` is available. */}
              {!contact ? (
                <div className={styles.skeletonContent}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <IonItem key={i} lines="full" className={styles.skeletonItem}>
                      <IonLabel>
                        <IonText>
                          <IonSkeletonText animated style={{ width: '70%' }} />
                        </IonText>
                        <IonText>
                          <IonSkeletonText animated style={{ width: '50%' }} />
                        </IonText>
                      </IonLabel>
                    </IonItem>
                  ))}
                </div>
              ) : contact ? (
                <>
                  {selectedSegment === 'info' && renderContactInfo()}
                  {selectedSegment === 'details' && (
                    <ContactDetails
                      parentIsLoadingDetailsData={isLoadingDetailsData}
                      parentUpcomingBookings={upcomingBookings}
                      parentPastBookings={pastBookings}
                      parentSubscriptions={subscriptions}
                      parentRawHelcimPlans={rawHelcimPlans}
                      parentHistoryStats={historyStats}
                      parentSelectedPeriod={selectedPeriod}
                      parentSetSelectedPeriod={setSelectedPeriod}
                      parentTasks={tasks}
                      parentBilling={billing}
                      parentSetBilling={setBilling}
                      parentContactId={id}
                      parentLoadMoreHistory={loadMoreHistory}
                      parentHasMoreHistory={hasMoreHistory}
                      parentIsLoadingMoreHistory={isLoadingMoreHistory}
                      parentContact={contact}
                      parentSetContact={setContact}
                      parentContactDetails={contactDetails}
                      parentSetContactDetails={setContactDetails}
                      parentContactDisplayName={contactDisplayName}
                      parentPresentToast={presentToast}
                      parentContactService={contactService}
                      parentSetTasks={setTasks}
                      parentApplyHistoryProgress={applyHistoryProgress}
                      parentHistoryHeaderRef={historyHeaderRef}
                      parentHistoryScrollRef={historyScrollRef}
                      parentHistoryTitleCardRef={historyTitleCardRef}
                      parentHandleHistoryScroll={handleHistoryScroll}
                      parentFormatTimeToString={formatTimeToString}
                      parentZohoContactId={externalIds.zohoContactId}
                    />
                  )}
                </>
              ) : (
                <div className={styles.noDataMessage}>
                  <IonText color="medium">{tr('pages.bookingSystem.Contact.contactNotFoundOrHasBeen')}</IonText>
                </div>
              )}
            </div>
            <div className={styles.scrollSpacer} />
          </IonGrid>
        </IonContent>

        {menuOpen && createPortal(
          <>
            <div
              ref={menuBackdropRef}
              className={styles.ellipsisBackdrop}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); }}
            />
            <div ref={menuCardRef} className={styles.ellipsisMenu}>
              {contactMenuOptions.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`${styles.ellipsisMenuItem} ${item.danger ? styles.ellipsisMenuItemDanger : ''}`}
                  onClick={() => runContactMenuItem(item.value)}
                >
                  {item.icon && <IonIcon icon={item.icon} className={styles.ellipsisMenuItemIcon} />}
                  {item.label}
                </button>
              ))}
            </div>
          </>,
          globalThis.document.body,
        )}

        <IonModal
          isOpen={showEditContactModal}
          onDidDismiss={() => setShowEditContactModal(false)}
          onWillDismiss={() => setShowEditContactModal(false)}
          canDismiss={true}
          backdropDismiss={true}
          keepContentsMounted={false}
          handle={false}
          className={styles.bookingsModal}
        >
          {contact && (
            <NewContact
              contactId={contact.id}
              onClose={() => {
                setShowEditContactModal(false);
                void loadContact();
              }}
            />
          )}
        </IonModal>

        <LabelSelectionModal
          isOpen={isLabelModalOpen}
          onClose={async () => {
            setIsLabelModalOpen(false);
          }}
          onSave={handleSaveLabels}
          availableLabels={availableLabels}
          currentLabelIds={contact?.labels || []}
        />

        <IonActionSheet
          isOpen={!!showDeleteConfirm}
          onDidDismiss={() => setShowDeleteConfirm(undefined)}
          header={tr('pages.bookingSystem.Contact.deleteAttachment')}
          cssClass="delete-contact-sheet"
          buttons={[
            {
              text: tr('pages.bookingSystem.Contact.delete'),
              role: 'destructive',
              handler: async () => {
                if (!contact || !showDeleteConfirm) return;
                try {
                  const success = await contactService.attachments.deleteAttachment(contact.id, showDeleteConfirm);
                  if (success) {
                    setContact(prev => ({
                      ...prev!,
                      attachments: prev?.attachments?.filter(a => a.id !== showDeleteConfirm) || []
                    }));
                  }
                } catch (error) {
                  createApiError(
                    ApiErrorCode.SERVER_ERROR,
                    'Error deleting attachment',
                    undefined,
                    { originalError: error }
                  );
                }
              }
            },
            {
              text: tr('pages.bookingSystem.Contact.cancel'),
              role: 'cancel'
            }
          ]}
        />
      </BasePageWithHeader>
    </PageWrapper>
  );

};

const ContactDetails: React.FC<{
  parentIsLoadingDetailsData: boolean;
  parentUpcomingBookings: BookingItem[];
  parentPastBookings: BookingItem[];
  parentSubscriptions: ContactSubscription[];
  parentRawHelcimPlans: HelcimPaymentPlan[];
  parentHistoryStats: { totalServices: number; totalSpent: number };
  parentSelectedPeriod: string;
  parentSetSelectedPeriod: (period: string) => void;
  parentTasks: TaskItem[];
  parentBilling: BillingConfig;
  parentSetBilling: (b: BillingConfig) => void;
  parentContactId: string;
  parentLoadMoreHistory: () => void;
  parentHasMoreHistory: boolean;
  parentIsLoadingMoreHistory: boolean;
  parentContact: ContactInterface | null;
  parentSetContact: React.Dispatch<React.SetStateAction<ContactInterface | null>>;
  parentContactDetails: ContactInterface | null;
  parentSetContactDetails: React.Dispatch<React.SetStateAction<ContactInterface | null>>;
  parentContactDisplayName: string;
  parentPresentToast: ReturnType<typeof useIonToast>[0];
  parentContactService: ReturnType<typeof DataServiceFactory.getService>;
  parentSetTasks: React.Dispatch<React.SetStateAction<TaskItem[]>>;
  parentApplyHistoryProgress: (progress: number, _smooth: boolean) => void;
  parentHistoryHeaderRef: React.RefObject<HTMLIonHeaderElement | null>;
  parentHistoryScrollRef: React.RefObject<HTMLIonGridElement | null>;
  parentHistoryTitleCardRef: React.RefObject<HTMLIonCardElement | null>;
  parentHandleHistoryScroll: (scrollTop: number) => void;
  parentFormatTimeToString: (time: string | Date) => string;
  parentZohoContactId: string;
}> = ({
  parentIsLoadingDetailsData,
  parentUpcomingBookings,
  parentPastBookings,
  parentSubscriptions,
  parentRawHelcimPlans,
  parentHistoryStats,
  parentSelectedPeriod,
  parentSetSelectedPeriod,
  parentTasks,
  parentBilling,
  parentSetBilling,
  parentContactId,
  parentLoadMoreHistory,
  parentHasMoreHistory,
  parentIsLoadingMoreHistory,
  parentContact,
  parentSetContact,
  parentContactDetails,
  parentSetContactDetails,
  parentContactDisplayName,
  parentPresentToast,
  parentContactService,
  parentSetTasks,
  parentApplyHistoryProgress,
  parentHistoryHeaderRef,
  parentHistoryScrollRef,
  parentHistoryTitleCardRef,
  parentHandleHistoryScroll,
  parentFormatTimeToString,
  parentZohoContactId,
}) => {
    const { t: tr } = useTranslation();
    const upcomingBookings = parentUpcomingBookings;
    const pastBookings = parentPastBookings;
    const billing = parentBilling;
    const setBilling = parentSetBilling;
    const contact = parentContact;
    const setContact = parentSetContact;
    const contactDetails = parentContactDetails;
    const setContactDetails = parentSetContactDetails;
    const contactDisplayName = parentContactDisplayName;
    const presentToast = parentPresentToast;
    const contactService = parentContactService;
    const setTasks = parentSetTasks;
    const applyHistoryProgress = parentApplyHistoryProgress;
    const historyHeaderRef = parentHistoryHeaderRef;
    const historyScrollRef = parentHistoryScrollRef;
    const historyTitleCardRef = parentHistoryTitleCardRef;
    const handleHistoryScroll = parentHandleHistoryScroll;
    const formatTimeToString = parentFormatTimeToString;
    const zohoContactId = parentZohoContactId;
    const [showInvoicesModal, setShowInvoicesModal] = useState(false);

    const [selectedRawSub, setSelectedRawSub] = useState<HelcimSubscription | null>(null);
    const [selectedSubPlanName, setSelectedSubPlanName] = useState<string | undefined>(undefined);
    const [selectedSubCard, setSelectedSubCard] = useState<Record<string, unknown> | null>(null);
    const [showSubDetail, setShowSubDetail] = useState(false);
    const [isSubDetailLoading, setIsSubDetailLoading] = useState(false);
    const [showManageSubs, setShowManageSubs] = useState(false);
    const id = parentContactId;
    const [isEditingBilling, setIsEditingBilling] = useState(false);
    const [isSavingBilling, setIsSavingBilling] = useState(false);
    const [billingDraft, setBillingDraft] = useState<Omit<BillingConfig, 'pricelistName' | 'itemName'>>({
      pricelistId: '', itemId: '', defaultRate: '',
      isInclusiveTax: true, taxPreference: 'taxable', taxId: '', taxName: '', taxPercentage: 0, invoiceMode: 'perOrder',
      isHelcimSubscriber: false,
    });
    const [zohoTaxes, setZohoTaxes] = useState<ZohoTax[]>([]);
    const [taxRatePickerOpen, setTaxRatePickerOpen] = useState(false);
    const taxRateTriggerRef = useRef<HTMLIonItemElement>(null);
    const [zohoItems, setZohoItems] = useState<ZohoItem[]>([]);
    const [zohoBaseItems, setZohoBaseItems] = useState<ZohoItem[]>([]);
    const [zohoPricelists, setZohoPricelists] = useState<ZohoPricelist[]>([]);
    const [loadingZoho, setLoadingZoho] = useState(false);
    const [pricelistPickerOpen, setPricelistPickerOpen] = useState(false);
    const [itemPickerOpen, setItemPickerOpen] = useState(false);
    const [conditionPickerOpen, setConditionPickerOpen] = useState(false);
    const [conditionFilter, setConditionFilter] = useState('');
    const pricelistTriggerRef = useRef<HTMLIonItemElement>(null);
    const itemTriggerRef = useRef<HTMLIonItemElement>(null);
    const conditionTriggerRef = useRef<HTMLIonItemElement>(null);
    const periodTriggerRef = useRef<HTMLButtonElement>(null);
    const [periodPickerOpen, setPeriodPickerOpen] = useState(false);
    const pickerPortalRef = useRef<HTMLDivElement>(null);
    const [showAllbookingsModal, setShowAllbookingsModal] = useState<boolean>(false);
    const [showHistoryDateSearch, setShowHistoryDateSearch] = useState<boolean>(false);
    const [historyDateFilter, setHistoryDateFilter] = useState<string>('');
    const historyDateSearchAnchorRef = useRef<HTMLIonButtonElement>(null);
    const historyPickerPortalRef = useRef<HTMLDivElement>(null);
    const subscriptions = parentSubscriptions;
    // "Active Subscriptions" should only ever show subscriptions the contact is
    // still actively paying on — expired/inactive ones stay out of this list
    // entirely (they're not "paused" either, so `paused` still counts as active
    // here since it's a billing-still-live state, just temporarily held).
    const activeSubscriptions = useMemo(
      () => subscriptions.filter(s => s.status === 'active' || s.status === 'paused'),
      [subscriptions]
    );
    const selectedPeriod = parentSelectedPeriod;
    const setSelectedPeriod = parentSetSelectedPeriod;
    const dataService = DataServiceFactory.getService();
    const [showNotes, setShowNotes] = useState(false);
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [editedNotes, setEditedNotes] = useState<string>('');
    const [showTasks, setShowTasks] = useState(false);
    const historyStats = parentHistoryStats;
    const tasks = parentTasks;
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [currentEditingField, setCurrentEditingField] = useState<'notes1' | 'notes2' | 'notes3'>('notes1');
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [showBookingDetailsModal, setShowBookingDetailsModal] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>(undefined);

    // The Firestore cache only holds ~90 days of history, so a contact with older
    // bookings can start with a list too short to actually scroll — which means the
    // scroll-triggered pagination in the modal below would never get a chance to
    // fire, and history would appear stuck at whatever the first page happened to
    // contain. While the modal is open, keep auto-fetching older pages until the
    // list is tall enough to scroll (or history runs out) — after that, further
    // pages only load as the user scrolls, same as before.
    useEffect(() => {
      if (!showAllbookingsModal) return;
      if (parentIsLoadingMoreHistory || !parentHasMoreHistory) return;

      const el = historyScrollRef.current as unknown as HTMLElement | null;
      const isScrollable = el ? el.scrollHeight > el.clientHeight + 20 : false;
      if (isScrollable) return;

      parentLoadMoreHistory();
    }, [showAllbookingsModal, parentIsLoadingMoreHistory, parentHasMoreHistory, pastBookings, historyScrollRef, parentLoadMoreHistory]);

    const formatDateLong = (dateString: string): string => {
      if (!dateString) return tr('pages.bookingSystem.Contact.noTimestamp');

      try {
        // Parse YYYY-MM-DD format from Wix directly
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dateString.split('-').map(Number);

          // Use en-US locale for consistent formatting with month names
          const date = new Date(year, month - 1, day);

          // Format using the same date components without timezone conversion
          return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }).format(date);
        }

        // Fallback to standard date parsing if not in YYYY-MM-DD format
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
          createApiError(
            ApiErrorCode.DATA_INVALID_FORMAT,
            'Invalid date string',
            undefined,
            { originalError: dateString }
          );
          return tr('pages.bookingSystem.Contact.invalidDate');
        }

        return date.toLocaleDateString(intlLocale(), {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        createApiError(
          ApiErrorCode.DATA_INVALID_FORMAT,
          'Error formatting date',
          undefined,
          { originalError: error, dateString }
        );
        return tr('pages.bookingSystem.Contact.invalidDate');
      }
    };

    const getTimestampFromCustomFields = (contactDetails: ContactInterface | null, fieldName: string): string => {
      if (!contactDetails || !contactDetails.notes || contactDetails.notes.length === 0) {
        return '';
      }

      // ✅ Map the field to the correct notes array index
      const fieldToIndex: { [key: string]: number } = {
        'notes1': 0,
        'notes2': 1,
        'notes3': 2
      };

      const index = fieldToIndex[fieldName];
      if (index === undefined || !contactDetails.notes[index]) {
        return '';
      }

      // ✅ Get the timestamp directly from the notes array
      const timestamp = contactDetails.notes[index].createdAt || contactDetails.notes[index].updatedAt;

      return timestamp || '';
    };

    const handleSaveNotes = async () => {
      if (!contact?.id) return;

      try {
        // Get the target field from state
        const targetField = currentEditingField;
        const noteIndex = targetField === 'notes1' ? 0 : targetField === 'notes2' ? 1 : 2;
        const existingNote = contactDetails?.notes?.[noteIndex];

        // Show a loading toast
        presentToast({
          message: tr('pages.bookingSystem.Contact.savingNotes'),
          duration: 2000,
          position: 'bottom'
        });

        let savedNote;
        if (existingNote?.id) {
          savedNote = await contactService.contacts.updateContactNote(
            contact.id,
            existingNote.id,
            editedNotes || '',
            existingNote.revision || '1'
          );
        } else {
          savedNote = await contactService.contacts.createContactNote(
            contact.id,
            editedNotes || ''
          );
        }

        // Show success toast
        presentToast({
          message: tr('pages.bookingSystem.Contact.notesSavedSuccessfully'),
          duration: 2000,
          position: 'bottom',
          color: 'success'
        });

        // Keep local contact state in sync for immediate UI update
        setContact(prevContact => {
          if (!prevContact) return null;
          const updatedNotes = [...(prevContact.notes || [])];
          while (updatedNotes.length <= noteIndex) {
            updatedNotes.push({
              id: '',
              content: '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }
          updatedNotes[noteIndex] = savedNote;
          return {
            ...prevContact,
            notes: updatedNotes,
          };
        });

        setContactDetails(prevContactDetails => {
          if (!prevContactDetails) return null;
          const updatedNotes = [...(prevContactDetails.notes || [])];
          while (updatedNotes.length <= noteIndex) {
            updatedNotes.push({
              id: '',
              content: '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }
          updatedNotes[noteIndex] = savedNote;
          return {
            ...prevContactDetails,
            notes: updatedNotes,
          };
        });

        // Exit edit mode
        setIsEditingNotes(false);
      } catch (error) {
        createApiError(
          ApiErrorCode.CONTACT_UPDATE_FAILED,
          'Error saving notes',
          undefined,
          { originalError: error }
        );
      }
    };

    // Function to handle adding more notes
    const handleAddMoreNotes = async () => {
      if (!contactDetails?.id) return;

      try {
        // First, get the current notes from the contact
        const notes1 = contactDetails.notes?.[0]?.content || '';
        const notes2 = contactDetails.notes?.[1]?.content || '';
        const notes3 = contactDetails.notes?.[2]?.content || '';

        // ...removed debug log...

        // Determine which field to use next
        let targetField: 'notes1' | 'notes2' | 'notes3' = 'notes1';

        if (!notes1.trim()) {
          // Use notes1 if empty
          targetField = 'notes1';
        } else if (!notes2.trim()) {
          // Use notes2 if notes1 is filled but notes2 is empty
          targetField = 'notes2';
        } else if (!notes3.trim()) {
          // Use notes3 if notes1 and notes2 are filled but notes3 is empty
          targetField = 'notes3';
        } else {
          // All fields are filled, use notes1 (oldest)
          targetField = 'notes1';
        }

        // ...removed debug log...

        // Set up for editing with empty content
        setEditedNotes('');
        setCurrentEditingField(targetField);
        setIsEditingNotes(true);

        // Ensure the notes section is visible
        if (!showNotes) {
          setShowNotes(true);
        }
      } catch (error) {
        createApiError(
          ApiErrorCode.CONTACT_UPDATE_FAILED,
          'Error preparing new note',
          undefined,
          { originalError: error }
        );
      }
    };



    /// Function to edit a specific note field
    const handleEditNoteField = (noteField: 'notes1' | 'notes2' | 'notes3') => {
      // Set the note content to edit based on which field was selected
      if (noteField === 'notes1') {
        setEditedNotes(contactDetails?.notes?.[0]?.content || '');
      } else if (noteField === 'notes2') {
        setEditedNotes(contactDetails?.notes?.[1]?.content || '');
      } else if (noteField === 'notes3') {
        setEditedNotes(contactDetails?.notes?.[2]?.content || '');
      }

      // Store which field we're editing in state
      setCurrentEditingField(noteField);

      // Enter edit mode
      setIsEditingNotes(true);
    };



    // Function to handle deleting a note
    const handleDeleteNoteField = async (noteField: 'notes1' | 'notes2' | 'notes3') => {
      if (!contactDetails?.id || !contact?.id) return;
      const confirm = window.confirm(tr('pages.bookingSystem.Contact.areYouSureYouWantTo2'));
      if (!confirm) return;

      try {
        const noteIndex = noteField === 'notes1' ? 0 : noteField === 'notes2' ? 1 : 2;
        const noteToDelete = contactDetails.notes?.[noteIndex];
        if (!noteToDelete?.id) {
          return;
        }

        await contactService.contacts.deleteContactNote(contact.id, noteToDelete.id);

        setContactDetails(prev => {
          if (!prev) return null;
          const updatedNotes = [...(prev.notes || [])];
          if (updatedNotes[noteIndex]) {
            updatedNotes[noteIndex] = {
              ...updatedNotes[noteIndex],
              id: '',
              content: '',
              revision: undefined,
              updatedAt: new Date().toISOString(),
            };
          }
          return { ...prev, notes: updatedNotes };
        });

        setContact(prev => {
          if (!prev) return null;
          const updatedNotes = [...(prev.notes || [])];
          if (updatedNotes[noteIndex]) {
            updatedNotes[noteIndex] = {
              ...updatedNotes[noteIndex],
              id: '',
              content: '',
              revision: undefined,
              updatedAt: new Date().toISOString(),
            };
          }
          return { ...prev, notes: updatedNotes };
        });

        presentToast({
          message: tr('pages.bookingSystem.Contact.noteDeletedSuccessfully'),
          duration: 2000,
          position: 'bottom',
          color: 'success'
        });
      } catch (error) {
        createApiError(
          ApiErrorCode.CONTACT_UPDATE_FAILED,
          'Error deleting note',
          undefined,
          { originalError: error }
        );
      }
    };
    // Add this function to handle adding a new note
    const handleAddNewNote = () => {
      // Determine which note field to use
      let targetField: 'notes1' | 'notes2' | 'notes3' = 'notes1';

      if (!contactDetails?.notes?.[0]?.content) {
        targetField = 'notes1';
      } else if (!contactDetails?.notes?.[1]?.content) {
        targetField = 'notes2';
      } else if (!contactDetails?.notes?.[2]?.content) {
        targetField = 'notes3';
      } else {
        // All fields are used, overwrite the oldest one (notes1)
        targetField = 'notes1';
      }

      // Store which field we're editing in state
      setCurrentEditingField(targetField);

      // Clear the edit field
      setEditedNotes('');

      // Enter edit mode
      setIsEditingNotes(true);
    };

    // Add this function to handle completing a task
    const handleCompleteTask = async (taskId: string) => {
      if (!contact) return; // Add early return if contact is null

      try {
        await dataService.tasks.completeContactTask(contact.id, taskId);
        // Update the local state
        setTasks(tasks.map(task =>
          task.id === taskId ? { ...task, status: 'completed', completedAt: new Date().toISOString() } : task
        ));
      } catch (error) {
        createApiError(
          ApiErrorCode.TASK_COMPLETION_FAILED,
          'Error completing task',
          undefined,
          { originalError: error }
        );
      }
    };

    // Update the handleAddTask function to explicitly type the priority
    const handleAddTask = async (taskData: TaskItem) => {
      if (!contact?.id) return;

      try {
        const createdTask = await dataService.tasks.addContactTask(contact.id, taskData);
        setTasks([...tasks, createdTask]);
        return Promise.resolve();
      } catch (error) {
        createApiError(
          ApiErrorCode.TASK_COMPLETION_FAILED,
          'Error adding task',
          undefined,
          { originalError: error }
        );
        return Promise.reject();
      }
    };

    // Add this function to get the display text for the current period
    const getPeriodDisplayText = () => {
      switch (selectedPeriod) {
        case '30': return tr('pages.bookingSystem.Contact.last30Days');
        case '60': return tr('pages.bookingSystem.Contact.last60Days');
        case '90': return tr('pages.bookingSystem.Contact.last90Days');
        case 'all': return tr('pages.bookingSystem.Contact.allTime');
        default: return tr('pages.bookingSystem.Contact.last30Days');
      }
    };

    // Helper functions for formatting
    const getDayNumber = (date: Date | string): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.getDate().toString();
    };

    const getMonthAbbr = (date: Date | string): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleString('default', { month: 'short' });
    };

    const getDayName = (date: Date | string): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleString('default', { weekday: 'short' });
    };

    // Format time from "14:30" to "2:30 PM"
    const formatTime = (timeStr: string): string => {
      try {
        // Handle ISO strings or time-only strings
        if (timeStr.includes('T')) {
          // If it's a full ISO datetime string
          const date = new Date(timeStr);
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
          // If it's just a time string like "14:30"
          const [hourStr, minute] = timeStr.split(':');
          const hour = parseInt(hourStr, 10);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const hour12 = hour % 12 || 12;
          return `${hour12}:${minute} ${ampm}`;
        }
      } catch (e) {
        createApiError(
          ApiErrorCode.DATA_INVALID_FORMAT,
          'Error formatting time',
          undefined,
          { originalError: e }
        );
        return timeStr; // Return original if parsing fails
      }
    };

    // Memoized so opening/closing an unrelated picker (or any other state
    // change in this large component) doesn't rebuild these lists — that
    // rebuild-on-every-render, not the portal itself, was the actual cause
    // of the Item dropdown feeling slow to open.
    const pricelistOptions = useMemo(() => [
      { label: tr('pages.bookingSystem.Contact.noPricelist'), value: '' },
      ...zohoPricelists.map(p => ({ label: p.name, value: p.pricelist_id })),
    ], [zohoPricelists, tr]);

    const parseItemCondition = useCallback((itemName: string) => {
      const parts = itemName.split('|').map(s => s.trim());
      return parts.length >= 3 ? parts[parts.length - 1] : '';
    }, []);

    const itemMatchesCondition = useCallback((itemName: string, filter: string) => {
      const selected = filter.trim().toLowerCase();
      if (!selected) return true;
      const parsed = parseItemCondition(itemName).trim().toLowerCase();
      return parsed === selected;
    }, [parseItemCondition]);

    const conditionValues = useMemo(() => {
      const knownOrder = ['Poor', 'Average', 'Good', 'Excellent'];
      const dynamicSet = new Set<string>();

      zohoItems.forEach(item => {
        const parsed = parseItemCondition(item.name);
        if (parsed) dynamicSet.add(parsed);
      });

      // Keep the familiar four common buckets first, then append any
      // additional condition values found in Zoho item names (e.g. commercial
      // variants) so nothing is silently hidden.
      const dynamicList = Array.from(dynamicSet);
      const orderedKnown = knownOrder.filter(k => dynamicList.some(v => v.toLowerCase() === k.toLowerCase()));
      const extra = dynamicList
        .filter(v => !knownOrder.some(k => k.toLowerCase() === v.toLowerCase()))
        .sort((a, b) => a.localeCompare(b));

      return [...orderedKnown, ...extra];
    }, [zohoItems, parseItemCondition]);

    const conditionOptions = useMemo(() => {
      const fallback = ['Poor', 'Average', 'Good', 'Excellent'];
      const values = conditionValues.length ? conditionValues : fallback;
      const localizeCondition = (value: string) => {
        const lower = value.toLowerCase();
        if (lower === 'poor') return tr('pages.bookingSystem.Contact.poor');
        if (lower === 'average') return tr('pages.bookingSystem.Contact.average');
        if (lower === 'good') return tr('pages.bookingSystem.Contact.good');
        if (lower === 'excellent') return tr('pages.bookingSystem.Contact.excellent');
        return translateDynamic(value);
      };

      return [
        { label: tr('pages.bookingSystem.Contact.allConditions'), value: '' },
        ...values.map(v => ({ label: localizeCondition(v), value: v })),
      ];
    }, [conditionValues, tr]);

    const itemOptions = useMemo(() => [
      { label: tr('pages.bookingSystem.Contact.selectItem'), value: '' },
      ...zohoItems
        .filter(i => itemMatchesCondition(i.name, conditionFilter))
        .map(i => ({ label: i.name, value: i.item_id, sublabel: `$${i.rate.toFixed(2)}` })),
    ], [zohoItems, conditionFilter, itemMatchesCondition, tr]);

    // Same anchored-portal picker as the pricelist/item/condition dropdowns
    // above, listing each distinct date a past booking exists on, newest
    // first — lets the user jump straight to a date instead of scrolling
    // the whole history list.
    const historyDateOptions = useMemo(() => {
      const uniqueDates = Array.from(new Set(pastBookings.map(b => b.date).filter(Boolean)));
      uniqueDates.sort((a, b) => b.localeCompare(a));
      return [
        { label: tr('pages.bookingSystem.Contact.allDates'), value: '' },
        ...uniqueDates.map(dateStr => {
          const parts = dateStr.split('-');
          const d = parts.length === 3
            ? new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
            : new Date(dateStr);
          return { label: `${getDayName(d)}, ${getMonthAbbr(d)} ${getDayNumber(d)}`, value: dateStr };
        }),
      ];
    }, [pastBookings, tr]);

    const filteredPastBookings = useMemo(
      () => historyDateFilter ? pastBookings.filter(b => b.date === historyDateFilter) : pastBookings,
      [pastBookings, historyDateFilter]
    );

    return (
      <IonList className={styles.detailsContainer}>
        <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
        {/* Circle Buttons Row (unchanged) */}
        <IonRow className={styles.circleButtonsContainer}>
          <IonCol className={styles.circleButtonWrapper}>
            <IonButton
              className={styles.circleButton}
              fill="clear"
              onClick={() => setShowInvoicesModal(true)}
            >
              <IonIcon icon={documentTextOutline} />
            </IonButton>
            <IonText className={styles.circleButtonLabel}>{tr('pages.bookingSystem.Contact.invoices')}</IonText>
          </IonCol>

          <IonCol className={styles.circleButtonWrapper}>
            <IonButton
              className={styles.circleButton}
              fill="clear"
              onClick={() => { }}
            >
              <IonIcon icon={cashOutline} />
            </IonButton>
            <IonText className={styles.circleButtonLabel}>{tr('pages.bookingSystem.Contact.paylink')}</IonText>
          </IonCol>
          <IonCol className={styles.circleButtonWrapper}>
            <IonButton
              className={styles.circleButton}
              fill="clear"
              onClick={() => { }}
            >
              <IonIcon icon={cardOutline} />
            </IonButton>
            <IonText className={styles.circleButtonLabel}>{tr('pages.bookingSystem.Contact.pos')}</IonText>
          </IonCol>

          <IonCol className={styles.circleButtonWrapper}>
            <IonButton
              className={styles.circleButton}
              fill="clear"
              onClick={() => {
                if (!contact) return; // Add early null check

                // Store the contact data for the booking modal
                const contactData = {
                  id: contact.id,
                  name: contactDisplayName,
                  email: contact.emails?.[0]?.email || '', // ✅ Access email through array
                  phone: contact.phones?.[0]?.phone || ''  // ✅ Access phone through array
                };

                // Store contact data for the booking modal
                DataStorageService.storeData('selectedContact', contactData);

                // Open the booking modal instead of navigating
                setIsBookingModalOpen(true);
              }}
            >
              <IonIcon icon={calendarOutline} />
            </IonButton>
            <IonText className={styles.circleButtonLabel}>{tr('pages.bookingSystem.Contact.book')}</IonText>
          </IonCol>
        </IonRow>

        {parentIsLoadingDetailsData ? (
          <div className={styles.skeletonContent}>
            {Array.from({ length: 8 }).map((_, i) => (
              <IonItem key={i} lines="full" className={styles.skeletonItem}>
                <IonLabel>
                  <IonText>
                    <IonSkeletonText animated style={{ width: '70%' }} />
                  </IonText>
                  <IonText>
                    <IonSkeletonText animated style={{ width: '50%' }} />
                  </IonText>
                </IonLabel>
              </IonItem>
            ))}
          </div>
        ) : (
          <>
            {/* Upcoming Bookings Section (unchanged) */}
            <IonCard className={styles.agendaContainer}>
              <IonItem lines="none" className={styles.sectionTitle}>
                <IonIcon icon={calendarOutline} slot="start" className={styles.sectionTitleIcon} />
                <IonLabel>{tr('pages.bookingSystem.Contact.upcomingBookings')}</IonLabel>
                <IonButton
                  fill="clear"
                  size="small"
                  className={styles.viewAllButton}
                  onClick={() => setShowAllbookingsModal(true)}
                >{tr('pages.bookingSystem.Contact.history')}
                </IonButton>
              </IonItem>

              <IonList className={styles.agendaList}>
                {upcomingBookings.length > 0 ? (
                  upcomingBookings.map((booking, index) => {

                    // Fix timezone issues by explicitly handling the date parsing
                    const dateStr = booking.date || '';
                    const startTime = booking.startTime; // Used for consistent time zone handling

                    // Parse date without timezone conversion
                    const dateParts = dateStr.split('-');
                    const bookingDate = dateParts.length === 3
                      ? new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]))
                      : new Date(dateStr);

                    // Use startTime in the component to avoid the "unused variable" warning:
                    const timeString = typeof startTime === 'string'
                      ? startTime
                      : startTime instanceof Date
                        ? `${startTime.getHours()}:${startTime.getMinutes().toString().padStart(2, '0')}`
                        : '';

                    return (
                      <IonCard
                        key={booking.id || `booking-${index}`}
                        className={styles.bookingCard}
                        button
                        onClick={() => {
                          setSelectedBookingId(booking.id);
                          setShowBookingDetailsModal(true);
                        }}
                      >
                        <IonItem className={styles.agendaItem} lines="none" detail={false}>
                          <IonCol size="auto" className={styles.dateCol}>
                            <IonText className={styles.dayNumber}>{getDayNumber(bookingDate)}</IonText>
                            <IonText className={styles.monthText}>{getMonthAbbr(bookingDate)}</IonText>
                          </IonCol>

                          <IonCol className={styles.bookingCol}>
                            <IonText
                              className={styles.serviceType}
                              style={{
                                color: getServiceColor(booking.serviceType as ServiceType)
                              }}
                            >
                              {translateDynamic(booking.serviceType)}
                            </IonText>
                            <IonText className={styles.bookingDetails}>
                              {getDayName(bookingDate)} • {formatTime(timeString)} • {booking.duration || '1h'}
                            </IonText>
                            <IonText className={styles.resourceInfo}>
                              {booking.resource || tr('pages.bookingSystem.Contact.noLocation')} • {booking.staff || tr('pages.bookingSystem.Contact.unassignedStaff')}
                            </IonText>
                          </IonCol>
                        </IonItem>
                      </IonCard>
                    );
                  })
                ) : (
                  <IonItem lines='none' className={styles.detailItem}>
                    <IonLabel>
                      <IonText color="medium" className={styles.emptyMessage}>{tr('pages.bookingSystem.Contact.noUpcomingBookings')}
                      </IonText>
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>
            </IonCard>

            {/* Show All Bookings Modal */}

            {/* Active Subscriptions Section */}
            <IonCard className={styles.subscriptionsContainer}>
              <IonItem className={styles.sectionTitle} lines="none">
                <IonIcon icon={refreshOutline} slot="start" className={styles.sectionTitleIcon} />
                <IonLabel>{tr('pages.bookingSystem.Contact.activeSubscriptions')}</IonLabel>
                <IonButton
                  size="small"
                  className={styles.viewAllButton}
                  disabled={activeSubscriptions.length === 0 || isSubDetailLoading}
                  onClick={async () => {
                    const subscription = activeSubscriptions[0];
                    if (!subscription) return;
                    setIsSubDetailLoading(true);
                    setSelectedSubPlanName(subscription.planName);
                    setSelectedSubCard(null);
                    const asHelcimSub: HelcimSubscription = {
                      id: parseInt(subscription.id),
                      status: subscription.status === 'expired' ? 'cancelled' : subscription.status,
                      recurringAmount: subscription.amount,
                      paymentMethod: subscription.paymentMethod,
                      paymentPlanId: parentRawHelcimPlans.find(p => p.name === subscription.planName)?.id,
                      dateBilling: subscription.nextBillingDate ? new Date(subscription.nextBillingDate).toISOString().split('T')[0] : undefined,
                      dateActivated: subscription.startDate instanceof Date ? subscription.startDate.toISOString().split('T')[0] : String(subscription.startDate).split('T')[0],
                    };
                    try {
                      const enriched = await getSubscriptionDetail(
                        parseInt(subscription.id),
                        { paymentPlanId: asHelcimSub.paymentPlanId, recurringAmount: asHelcimSub.recurringAmount }
                      );
                      const merged = { ...asHelcimSub, ...(enriched ?? {}) } as HelcimSubscription;
                      const customerCode = (enriched as Record<string, unknown>)?.['customerCode'] as string | undefined;
                      let card: Record<string, unknown> | null = null;
                      if (customerCode) {
                        try {
                          const customer = await getHelcimCustomer(customerCode);
                          if (merged.paymentMethod === 'bank') {
                            const banks = ((customer?.['bankAccounts'] ?? []) as Record<string, unknown>[]);
                            const bank = banks.find(b => b['isDefault'] === 1 || b['isDefault'] === true) ?? banks[0] ?? null;
                            card = bank ? { ...bank, _type: 'bank' } : null;
                          } else {
                            const cards = ((customer?.['cards'] ?? []) as Record<string, unknown>[]);
                            card = cards.find(c => c['isDefault'] === 1 || c['isDefault'] === true || c['default'] === true || c['setDefault'] === 1) ?? cards[0] ?? null;
                          }
                        } catch { }
                      }
                      setSelectedRawSub(merged);
                      setSelectedSubCard(card);
                    } catch {
                      setSelectedRawSub(asHelcimSub);
                    } finally {
                      setIsSubDetailLoading(false);
                      setShowSubDetail(true);
                    }
                  }}
                >
                  {isSubDetailLoading ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.manage')}
                </IonButton>
              </IonItem>

              <IonList>
                {activeSubscriptions.length > 0 ? (
                  activeSubscriptions.map((subscription, index) => {
                    const dayLabel = subscription.dayOfWeek || subscription.dayOfMonth;
                    const amtLabel = `$${subscription.amount.toFixed(2)}`;
                    const freqLabel = subscription.frequency;
                    // planName falls back to "Plan #<id>" when Helcim has no named
                    // plan for this subscription — split that fallback so the
                    // heading always reads as a name and the number becomes the
                    // subtitle, instead of showing the raw number as the heading.
                    const planNumberMatch = subscription.planName.match(/^Plan #(.+)$/);
                    const displayPlanName = planNumberMatch ? tr('pages.bookingSystem.Contact.subscriptionPlan') : subscription.planName;
                    return (
                      <IonCard
                        key={subscription.id || `subscription-${index}`}
                        className={styles.subscriptionCard}
                      >
                        <IonCardContent className={styles.subscriptionCardContent}>
                          <p className={styles.subscriptionHeader}>
                            <span className={styles.subscriptionBadgeRow}>
                              <IonBadge className={`${styles.subscriptionStatus} ${styles[SUBSCRIPTION_BADGE_TONE_CLASS[SUBSCRIPTION_STATUS_COLOR[subscription.status] ?? 'medium']]}`}>
                                {translateDynamic(subscription.status)}
                              </IonBadge>
                            </span>
                            <span className={styles.subscriptionBlock}>
                              <IonText className={styles.subscriptionName}>{displayPlanName}</IonText>
                            </span>
                          </p>
                          <span className={styles.subStatsRow}>
                            <span className={styles.subStat}>
                              <IonIcon icon={cashOutline} className={`${styles.subStatIcon} ${styles.subStatIconGreen}`} />
                              <span className={styles.subStatValue}>{amtLabel}</span>
                              <span className={styles.subStatLabel}>{tr('pages.bookingSystem.Contact.amount')}</span>
                            </span>
                            <span className={styles.subStatDivider} />
                            <span className={styles.subStat}>
                              <IonIcon icon={refreshCircleOutline} className={`${styles.subStatIcon} ${styles.subStatIconBlue}`} />
                              <span className={styles.subStatValue}>{capitalizeFirstLetter(freqLabel)}{dayLabel ? ` (${dayLabel})` : ''}</span>
                              <span className={styles.subStatLabel}>{tr('pages.bookingSystem.Contact.frequency')}</span>
                            </span>
                            <span className={styles.subStatDivider} />
                            <span className={styles.subStat}>
                              <IonIcon icon={calendarOutline} className={`${styles.subStatIcon} ${styles.subStatIconPurple}`} />
                              <span className={styles.subStatValue}>{new Date(subscription.nextBillingDate).toLocaleDateString(intlLocale())}</span>
                              <span className={styles.subStatLabel}>{tr('pages.bookingSystem.Contact.nextBilling')}</span>
                            </span>
                          </span>
                        </IonCardContent>
                      </IonCard>
                    );
                  })
                ) : (
                  <IonItem lines='none' className={styles.detailItem}>
                    <IonLabel>
                      <IonText color="medium" className={styles.emptyMessage}>{tr('pages.bookingSystem.Contact.noActiveSubscriptions')}
                      </IonText>
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>
            </IonCard>

            <HelcimSubscriptionDetailModal
              isOpen={showSubDetail}
              subscription={selectedRawSub}
              planName={selectedSubPlanName ?? parentRawHelcimPlans.find(p => p.id === selectedRawSub?.paymentPlanId)?.name}
              customerName={contact ? contactDisplayName : undefined}
              cardData={selectedSubCard}
              onClose={() => { setShowSubDetail(false); setSelectedRawSub(null); setSelectedSubPlanName(undefined); setSelectedSubCard(null); }}
              onManage={async (sub, action) => {
                if (action !== 'edit') {
                  await manageSubscription(sub.id, action as 'pause' | 'resume' | 'cancel' | 'delete');
                }
                setShowSubDetail(false);
                setSelectedRawSub(null);
              }}
            />

            <HelcimSubscriptionsModal
              isOpen={showManageSubs}
              onClose={() => setShowManageSubs(false)}
              title={tr('pages.bookingSystem.Contact.subscriptions')}
            />

            {/* Billing Config */}
            <IonCard className={styles.subscriptionsContainer}>
              <IonItem className={styles.sectionTitle} lines="none">
                <IonIcon icon={cashOutline} slot="start" className={styles.sectionTitleIcon} />
                <IonLabel>{tr('pages.bookingSystem.Contact.billingConfig')}</IonLabel>
                {isEditingBilling && loadingZoho && (
                  <IonSpinner name="dots" slot="end" className={styles.sectionTitleSpinner} />
                )}
                {!isEditingBilling && (
                  <IonButton fill="clear" size="small" slot="end" onClick={async () => {
                    setBillingDraft({
                      pricelistId: billing.pricelistId, itemId: billing.itemId, defaultRate: billing.defaultRate,
                      isInclusiveTax: billing.isInclusiveTax, taxPreference: billing.taxPreference,
                      taxId: billing.taxId, taxName: billing.taxName, taxPercentage: billing.taxPercentage,
                      invoiceMode: billing.invoiceMode, isHelcimSubscriber: billing.isHelcimSubscriber,
                    });
                    setIsEditingBilling(true);
                    if (!zohoBaseItems.length) {
                      setLoadingZoho(true);
                      try {
                        const [pricing, taxes] = await Promise.all([
                          fetchZohoPricingData(),
                          fetchZohoTaxes(),
                        ]);
                        setZohoBaseItems(pricing.items);
                        setZohoPricelists(pricing.pricelists);
                        setZohoTaxes(taxes);
                        if (billing.pricelistId) {
                          const plItems = await fetchZohoPricelistItems(billing.pricelistId);
                          if (plItems.length) {
                            const rateMap = new Map(plItems.map(i => [i.item_id, i.rate]));
                            const merged = pricing.items.map(i => rateMap.has(i.item_id) ? { ...i, rate: rateMap.get(i.item_id)! } : i);
                            const mergedIds = new Set(merged.map(i => i.item_id));
                            const pricelistOnly = plItems.filter(i => !mergedIds.has(i.item_id));
                            setZohoItems([...merged, ...pricelistOnly]);
                          } else {
                            setZohoItems(pricing.items);
                          }
                        } else { setZohoItems(pricing.items); }
                      } catch (e) { console.error('fetchZohoPricingData failed', e); }
                      setLoadingZoho(false);
                    }
                  }}>
                    <IonIcon icon={pencilOutline} />
                  </IonButton>
                )}
              </IonItem>
              <IonList>
                {isEditingBilling ? (
                  <>
                    <IonItem lines="none" className={styles.editField}>
                      <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.pricelist')}</IonLabel>
                      <IonItem
                        ref={pricelistTriggerRef}
                        button
                        detail={false}
                        lines="none"
                        className={styles.pickerTrigger}
                        onClick={() => setPricelistPickerOpen(true)}
                      >
                        <IonLabel>{zohoPricelists.find(p => p.pricelist_id === billingDraft.pricelistId)?.name || tr('pages.bookingSystem.Contact.noPricelist')}</IonLabel>
                        <IonIcon icon={chevronDownOutline} slot="end" />
                      </IonItem>
                    </IonItem>
                    <IonItem lines="none" className={styles.editField}>
                      <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.filterByCondition')}</IonLabel>
                      <IonItem
                        ref={conditionTriggerRef}
                        button
                        detail={false}
                        lines="none"
                        className={styles.pickerTrigger}
                        onClick={() => setConditionPickerOpen(true)}
                      >
                        <IonLabel>{conditionFilter ? tr('pages.bookingSystem.Contact.condition', { conditionFilter: translateDynamic(conditionFilter) }) : tr('pages.bookingSystem.Contact.allConditions')}</IonLabel>
                        <IonIcon icon={chevronDownOutline} slot="end" />
                      </IonItem>
                    </IonItem>
                    <IonItem lines="none" className={styles.editField} style={{ marginBottom: 8 }}>
                      <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.item')}</IonLabel>
                      <IonItem
                        ref={itemTriggerRef}
                        button
                        detail={false}
                        lines="none"
                        className={styles.pickerTrigger}
                        onClick={() => setItemPickerOpen(true)}
                      >
                        <IonLabel className="ion-text-wrap">{zohoItems.find(i => i.item_id === billingDraft.itemId)?.name || tr('pages.bookingSystem.Contact.selectItem')}</IonLabel>
                        <IonIcon icon={chevronDownOutline} slot="end" />
                      </IonItem>
                    </IonItem>
                    <IonItem lines="none" className={styles.editField}>
                      <IonLabel position="stacked">{billingDraft.isHelcimSubscriber ? tr('pages.bookingSystem.Contact.finalPriceOverrideOptional') : tr('pages.bookingSystem.Contact.rateOverrideOptional')}</IonLabel>
                      <IonItem lines="none" className={styles.priceInputBox}>
                        <span slot="start" className={styles.priceInputPrefix}>$</span>
                        <IonInput
                          className={styles.priceInputField}
                          type="text"
                          inputmode="decimal"
                          placeholder="0.00"
                          value={billingDraft.defaultRate}
                          onIonInput={(e) => {
                            const digits = String(e.detail.value ?? '').replace(/\D/g, '');
                            if (digits === '') { setBillingDraft(prev => ({ ...prev, defaultRate: '' })); return; }
                            const cents = parseInt(digits, 10);
                            setBillingDraft(prev => ({ ...prev, defaultRate: (cents / 100).toFixed(2) }));
                          }}
                          onIonBlur={() => {
                            const n = parseFloat(billingDraft.defaultRate);
                            if (!isNaN(n)) setBillingDraft(prev => ({ ...prev, defaultRate: n.toFixed(2) }));
                          }}
                        />
                      </IonItem>
                      {/* Two different meanings depending on isHelcimSubscriber (see that field's
                          own doc comment in zohoServiceOrderService.ts): for a normal
                          exclusive-tax client this number IS the pre-tax rate and Zoho adds tax
                          on top, so show that the real total is HIGHER than what's typed. For a
                          Helcim-subscription client only, this is "the final total to match
                          Helcim's own invoice" and gets back-converted to a pre-tax rate.
                          Confirmed real bug (2026-10-13): applying the back-conversion to every
                          exclusive-tax client silently shrank the rate so Zoho's total stayed
                          equal to the typed price, defeating the point of choosing Exclusive. */}
                      {(() => {
                        let helperText = '';
                        if (!billingDraft.isInclusiveTax && billingDraft.taxPercentage > 0) {
                          const typed = parseFloat(billingDraft.defaultRate);
                          if (!isNaN(typed) && typed > 0) {
                            if (billingDraft.isHelcimSubscriber) {
                              const preTaxRate = Math.round((typed / (1 + billingDraft.taxPercentage / 100)) * 100) / 100;
                              const taxAmount = Math.round((typed - preTaxRate) * 100) / 100;
                              helperText = `${tr('pages.bookingSystem.Contact.sentToZohoAsRate')}${preTaxRate.toFixed(2)}${tr('pages.bookingSystem.Contact.tax')}${taxAmount.toFixed(2)} (${billingDraft.taxPercentage}%) = $${typed.toFixed(2)}`;
                            } else {
                              const taxAmount = Math.round((typed * billingDraft.taxPercentage / 100) * 100) / 100;
                              const total = Math.round((typed + taxAmount) * 100) / 100;
                              helperText = `${tr('pages.bookingSystem.Contact.rate2')}${typed.toFixed(2)}${tr('pages.bookingSystem.Contact.tax')}${taxAmount.toFixed(2)} (${billingDraft.taxPercentage}%) = $${total.toFixed(2)}${tr('pages.bookingSystem.Contact.total')}`;
                            }
                          }
                        }

                        return (
                          <IonText
                            color="medium"
                            className={`${styles.priceHelperText} ${helperText ? '' : styles.priceHelperTextHidden}`}
                          >
                            {helperText || ' '}
                          </IonText>
                        );
                      })()}
                    </IonItem>
                    <IonItem lines="none" className={styles.editField}>
                      <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.taxHandling')}</IonLabel>
                      <IonItem lines="none" className={styles.billingControlRow}>
                        <IonToggle
                          slot="start"
                          checked={billingDraft.isInclusiveTax}
                          onIonChange={(e) => setBillingDraft(prev => ({ ...prev, isInclusiveTax: e.detail.checked }))}
                        />
                        <IonText className={styles.billingControlLabel} onPointerDown={swallowControlTextClick} onClick={swallowControlTextClick}>
                          {billingDraft.isInclusiveTax ? tr('pages.bookingSystem.Contact.inclusiveTaxIncludedInPrice') : tr('pages.bookingSystem.Contact.exclusiveTaxAddedToPrice')}
                        </IonText>
                      </IonItem>
                    </IonItem>
                    <IonItem lines="none" className={styles.editField}>
                      <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.recurringHelcimSubscription')}</IonLabel>
                      <IonItem lines="none" className={styles.billingControlRow}>
                        <IonToggle
                          slot="start"
                          checked={billingDraft.isHelcimSubscriber}
                          onIonChange={(e) => setBillingDraft(prev => ({ ...prev, isHelcimSubscriber: e.detail.checked }))}
                        />
                        <IonText className={styles.billingControlLabel} onPointerDown={swallowControlTextClick} onClick={swallowControlTextClick}>
                          {billingDraft.isHelcimSubscriber ? tr('pages.bookingSystem.Contact.onExclusiveRateIsTheFinal') : tr('pages.bookingSystem.Contact.offNormalBillingTaxAddedOn')}
                        </IonText>
                      </IonItem>
                    </IonItem>
                    <IonItem lines="none" className={styles.billingControlDivider} />
                    <IonItem lines="none" className={styles.editField}>
                      <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.invoicing')}</IonLabel>
                      <IonRadioGroup
                        value={billingDraft.invoiceMode}
                        onIonChange={e => setBillingDraft(prev => ({ ...prev, invoiceMode: e.detail.value }))}
                        className={styles.billingRadioGroup}
                      >
                        <IonRow className={styles.billingRadioOptionRow}>
                          <IonCol size="auto"><IonRadio value="perOrder" /></IonCol>
                          <IonCol className={styles.billingRadioLabelCol}><IonText className={styles.billingRadioLabel}>{tr('pages.bookingSystem.Contact.invoicePerOrder')}</IonText></IonCol>
                        </IonRow>
                        <IonText color="medium" className={styles.billingRadioHint}>{tr('pages.bookingSystem.Contact.invoicePerOrderHint')}</IonText>

                        <IonRow className={styles.billingRadioOptionRow}>
                          <IonCol size="auto"><IonRadio value="grouped" /></IonCol>
                          <IonCol className={styles.billingRadioLabelCol}><IonText className={styles.billingRadioLabel}>{tr('pages.bookingSystem.Contact.invoiceGrouped')}</IonText></IonCol>
                        </IonRow>
                        <IonText color="medium" className={styles.billingRadioHint}>{tr('pages.bookingSystem.Contact.invoiceGroupedHint')}</IonText>

                        <IonRow className={styles.billingRadioOptionRow}>
                          <IonCol size="auto"><IonRadio value="manual" /></IonCol>
                          <IonCol className={styles.billingRadioLabelCol}><IonText className={styles.billingRadioLabel}>{tr('pages.bookingSystem.Contact.invoiceManual')}</IonText></IonCol>
                        </IonRow>
                        <IonText color="medium" className={styles.billingRadioHint}>{tr('pages.bookingSystem.Contact.invoiceManualHint')}</IonText>
                      </IonRadioGroup>
                    </IonItem>
                    <IonItem lines="none" className={styles.billingControlDivider} />
                    <IonItem lines="none" className={styles.editField}>
                      <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.taxPreference')}</IonLabel>
                      <IonRadioGroup
                        value={billingDraft.taxPreference}
                        onIonChange={e => setBillingDraft(prev => ({ ...prev, taxPreference: e.detail.value }))}
                        className={styles.billingTaxPreferenceGroup}
                      >
                        <IonRow className={styles.billingTaxPreferenceOption}>
                          <IonCol size="auto"><IonRadio value="taxable" /></IonCol>
                          <IonCol size="auto" className={styles.billingRadioLabelCol}><IonText className={styles.billingRadioLabel}>{tr('pages.bookingSystem.Contact.taxable')}</IonText></IonCol>
                        </IonRow>
                        <IonRow className={styles.billingTaxPreferenceOption}>
                          <IonCol size="auto"><IonRadio value="exempt" /></IonCol>
                          <IonCol size="auto" className={styles.billingRadioLabelCol}><IonText className={styles.billingRadioLabel}>{tr('pages.bookingSystem.Contact.taxExempt')}</IonText></IonCol>
                        </IonRow>
                      </IonRadioGroup>
                    </IonItem>
                    {billingDraft.taxPreference === 'taxable' && (
                      <IonItem lines="none" className={styles.editField}>
                        <IonLabel position="stacked">{tr('pages.bookingSystem.Contact.taxRate')}</IonLabel>
                        <IonItem
                          ref={taxRateTriggerRef}
                          button
                          detail={false}
                          lines="none"
                          className={styles.pickerTrigger}
                          onClick={() => setTaxRatePickerOpen(true)}
                        >
                          <IonLabel className="ion-text-wrap">
                            {billingDraft.taxName ? `${billingDraft.taxName} [${billingDraft.taxPercentage}%]` : tr('pages.bookingSystem.Contact.selectTaxRate')}
                          </IonLabel>
                          <IonIcon icon={chevronDownOutline} slot="end" />
                        </IonItem>
                      </IonItem>
                    )}
                    <IonItem lines="none" className={styles.editActions}>
                      <IonButton fill="solid" size="small" className={`${styles.pillButton} ${styles.pillButtonPrimary}`} disabled={loadingZoho || isSavingBilling} onClick={async () => {
                        const selPl = zohoPricelists.find(p => p.pricelist_id === billingDraft.pricelistId);
                        const selItem = zohoItems.find(i => i.item_id === billingDraft.itemId);
                        const rateVal = parseFloat(billingDraft.defaultRate);
                        setIsSavingBilling(true);
                        try {
                          await setDoc(doc(dbContacts, 'contactBilling', id), {
                            defaultPricelistId: billingDraft.pricelistId || null,
                            defaultPricelistName: selPl?.name || null,
                            defaultItemId: billingDraft.itemId || null,
                            defaultItemName: selItem?.name || null,
                            defaultRate: isNaN(rateVal) ? null : rateVal,
                            isInclusiveTax: billingDraft.isInclusiveTax,
                            taxPreference: billingDraft.taxPreference,
                            taxId: billingDraft.taxPreference === 'taxable' ? (billingDraft.taxId || null) : null,
                            taxName: billingDraft.taxPreference === 'taxable' ? (billingDraft.taxName || null) : null,
                            taxPercentage: billingDraft.taxPreference === 'taxable' ? billingDraft.taxPercentage : null,
                            invoiceMode: billingDraft.invoiceMode,
                            isHelcimSubscriber: billingDraft.isHelcimSubscriber,
                          });
                          const savedBilling: BillingConfig = {
                            pricelistId: billingDraft.pricelistId,
                            pricelistName: selPl?.name || '',
                            itemId: billingDraft.itemId,
                            itemName: selItem?.name || '',
                            defaultRate: isNaN(rateVal) ? '' : String(rateVal),
                            isInclusiveTax: billingDraft.isInclusiveTax,
                            taxPreference: billingDraft.taxPreference,
                            taxId: billingDraft.taxPreference === 'taxable' ? billingDraft.taxId : '',
                            taxName: billingDraft.taxPreference === 'taxable' ? billingDraft.taxName : '',
                            taxPercentage: billingDraft.taxPreference === 'taxable' ? billingDraft.taxPercentage : 0,
                            invoiceMode: billingDraft.invoiceMode,
                            isHelcimSubscriber: billingDraft.isHelcimSubscriber,
                          };
                          setBilling(savedBilling);
                          // Patch the same localStorage snapshot useIonViewWillEnter's
                          // cache-hit path reads from (same pattern as the live-contact
                          // subscription above) — otherwise navigating away and back
                          // within the 5-minute cache window would show the billing
                          // values from before this save, even though Firestore and
                          // this page's own state are already correct.
                          try {
                            const cached = localStorage.getItem(`contact-${id}`);
                            const base = cached ? JSON.parse(cached) : {};
                            localStorage.setItem(`contact-${id}`, JSON.stringify({ ...base, billing: savedBilling }));
                            localStorage.setItem(`contact-${id}-time`, Date.now().toString());
                          } catch { /* cache write is best-effort */ }
                          // Pricelist and tax live only in contactBilling — push them to the
                          // Zoho customer too (same as ContactDetailDesktop). Kept after the
                          // Firestore write/state update so a Zoho failure can't hide the save.
                          if (zohoContactId) {
                            try {
                              if (billingDraft.pricelistId !== billing.pricelistId) {
                                await updateZohoContact(zohoContactId, { pricebook_id: billingDraft.pricelistId || null });
                              }
                              if (billingDraft.taxPreference !== billing.taxPreference || billingDraft.taxId !== billing.taxId) {
                                await updateZohoContact(zohoContactId, {
                                  is_taxable: billingDraft.taxPreference === 'taxable',
                                  ...(billingDraft.taxPreference === 'taxable' && billingDraft.taxId ? { tax_id: billingDraft.taxId } : {}),
                                });
                              }
                            } catch (e) { console.error('Failed to push billing config to Zoho', e); }
                          }
                        } catch (e) { console.error('Failed to save billing config', e); }
                        setIsSavingBilling(false);
                        setIsEditingBilling(false);
                      }}>{isSavingBilling ? <IonSpinner name="dots" /> : tr('pages.bookingSystem.Contact.save')}</IonButton>
                      <IonButton size="small" fill="clear" className={`${styles.pillButton} ${styles.pillButtonSecondary}`} onClick={() => setIsEditingBilling(false)}>{tr('pages.bookingSystem.Contact.cancel')}</IonButton>
                    </IonItem>
                  </>
                ) : (
                  <>
                    <IonItem lines="none" className={`${styles.navRow} ${styles.navRowCompact}`}>
                      <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                        <IonIcon icon={listOutline} />
                      </IonText>
                      <IonLabel>
                        <p className={styles.infoRowMeta}>
                          <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.pricelist')}</IonText>
                        </p>
                        <h3>{billing.pricelistName || tr('pages.bookingSystem.Contact.notSet')}</h3>
                      </IonLabel>
                    </IonItem>
                    <IonItem lines="none" className={`${styles.navRow} ${styles.navRowCompact}`}>
                      <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                        <IonIcon icon={pricetagOutline} />
                      </IonText>
                      <IonLabel>
                        <p className={styles.infoRowMeta}>
                          <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.item')}</IonText>
                        </p>
                        <h3>{billing.itemName || tr('pages.bookingSystem.Contact.notSet')}</h3>
                      </IonLabel>
                    </IonItem>
                    <IonItem lines="none" className={`${styles.navRow} ${styles.navRowCompact}`}>
                      <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                        <IonIcon icon={cashOutline} />
                      </IonText>
                      <IonLabel>
                        <p className={styles.infoRowMeta}>
                          <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.rate')}</IonText>
                        </p>
                        <h3>
                          {billing.defaultRate ? tr('pages.bookingSystem.Contact.override', { v1: parseFloat(billing.defaultRate).toFixed(2) }) : tr('pages.bookingSystem.Contact.fromPricelist')}
                        </h3>
                      </IonLabel>
                    </IonItem>
                    <IonItem lines="none" className={`${styles.navRow} ${styles.navRowCompact}`}>
                      <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                        <IonIcon icon={cashOutline} />
                      </IonText>
                      <IonLabel>
                        <p className={styles.infoRowMeta}>
                          <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.taxHandling')}</IonText>
                        </p>
                        <h3>{billing.isInclusiveTax ? tr('pages.bookingSystem.Contact.inclusive') : tr('pages.bookingSystem.Contact.exclusive')}{billing.taxPreference === 'taxable' && billing.taxName ? ` · ${billing.taxName} [${billing.taxPercentage}%]` : ''}</h3>
                      </IonLabel>
                    </IonItem>
                    {!billing.isInclusiveTax && (
                      <IonItem lines="none" className={`${styles.navRow} ${styles.navRowCompact}`}>
                        <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                          <IonIcon icon={cashOutline} />
                        </IonText>
                        <IonLabel>
                          <p className={styles.infoRowMeta}>
                            <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.recurringHelcimSubscription')}</IonText>
                          </p>
                          <h3>{billing.isHelcimSubscriber ? tr('pages.bookingSystem.Contact.yesRateIsBackCalculatedFrom') : tr('pages.bookingSystem.Contact.no')}</h3>
                        </IonLabel>
                      </IonItem>
                    )}
                    <IonItem lines="none" className={`${styles.navRow} ${styles.navRowCompact}`}>
                      <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                        <IonIcon icon={cashOutline} />
                      </IonText>
                      <IonLabel>
                        <p className={styles.infoRowMeta}>
                          <IonText className={styles.rowLabel}>{tr('pages.bookingSystem.Contact.invoicing')}</IonText>
                        </p>
                        <h3>{billing.invoiceMode === 'grouped' ? tr('pages.bookingSystem.Contact.invoiceGrouped') : billing.invoiceMode === 'manual' ? tr('pages.bookingSystem.Contact.invoiceManual') : tr('pages.bookingSystem.Contact.invoicePerOrder')}</h3>
                      </IonLabel>
                    </IonItem>
                    {billing.invoiceMode === 'grouped' && <GroupedInvoiceCard zohoContactId={parentZohoContactId} />}
                  </>
                )}
              </IonList>
            </IonCard>

            <PortalDropdown
              isOpen={taxRatePickerOpen}
              anchorRef={taxRateTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setTaxRatePickerOpen(false)}
              options={zohoTaxes.map(t => ({ label: `${t.tax_name} [${t.tax_percentage}%]`, value: t.tax_id }))}
              value={billingDraft.taxId}
              searchable
              onSelect={(v) => {
                const tax = zohoTaxes.find(t => t.tax_id === String(v));
                setBillingDraft(prev => ({
                  ...prev,
                  taxId: String(v),
                  taxName: tax?.tax_name || '',
                  taxPercentage: tax?.tax_percentage || 0,
                }));
                setTaxRatePickerOpen(false);
              }}
            />

            <PortalDropdown
              isOpen={pricelistPickerOpen}
              anchorRef={pricelistTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setPricelistPickerOpen(false)}
              options={pricelistOptions}
              value={billingDraft.pricelistId}
              searchable
              onSelect={async (v) => {
                const pid = String(v);
                setBillingDraft(prev => ({ ...prev, pricelistId: pid }));
                setPricelistPickerOpen(false);
                let nextItems = zohoBaseItems;
                if (pid && zohoBaseItems.length) {
                  try {
                    const plItems = await fetchZohoPricelistItems(pid);
                    const rateMap = new Map(plItems.map(i => [i.item_id, i.rate]));
                    if (plItems.length) {
                      const merged = zohoBaseItems.map(i => rateMap.has(i.item_id) ? { ...i, rate: rateMap.get(i.item_id)! } : i);
                      const mergedIds = new Set(merged.map(i => i.item_id));
                      const pricelistOnly = plItems.filter(i => !mergedIds.has(i.item_id));
                      nextItems = [...merged, ...pricelistOnly];
                    }
                  } catch { /* fall back to base rates */ }
                }
                setZohoItems(nextItems);
                // Keep the selected item; just refresh its rate from the new pricelist.
                setBillingDraft(prev => {
                  const item = nextItems.find(i => i.item_id === prev.itemId);
                  return item ? { ...prev, defaultRate: item.rate.toFixed(2) } : prev;
                });
              }}
            />

            <PortalDropdown
              isOpen={conditionPickerOpen}
              anchorRef={conditionTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setConditionPickerOpen(false)}
              options={conditionOptions}
              value={conditionFilter}
              onSelect={(v) => {
                setConditionFilter(v);
                setConditionPickerOpen(false);
              }}
            />

            <PortalDropdown
              isOpen={itemPickerOpen}
              anchorRef={itemTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setItemPickerOpen(false)}
              options={itemOptions}
              value={billingDraft.itemId}
              searchable
              onSelect={(v) => {
                setBillingDraft(prev => {
                  const item = zohoItems.find(i => i.item_id === String(v));
                  return {
                    ...prev,
                    itemId: String(v),
                    defaultRate: item ? item.rate.toFixed(2) : prev.defaultRate,
                  };
                });
                setItemPickerOpen(false);
              }}
            />

            {/* Client History Section */}
            <IonCard className={styles.clientHistoryContainer}>
              <IonList>
                <IonItem lines="none" className={styles.sectionTitle}>
                  <IonIcon icon={timeOutline} slot="start" className={styles.sectionTitleIcon} />
                  <IonLabel>{tr('pages.bookingSystem.Contact.clientHistory')}</IonLabel>
                </IonItem>

                {/* Period selector dropdown - Better aligned under the title */}
                <IonItem lines="none" className={styles.periodSelectorContainer}>
                  <button
                    ref={periodTriggerRef}
                    className={`${styles.periodSelectorButton} ${periodPickerOpen ? styles.isOpen : ''}`}
                    onClick={() => setPeriodPickerOpen(v => !v)}
                  >
                    {getPeriodDisplayText()} <IonIcon icon={caretDown} size="small" />
                  </button>
                </IonItem>

                <PortalDropdown
                  isOpen={periodPickerOpen}
                  anchorRef={periodTriggerRef}
                  portalRef={pickerPortalRef}
                  onClose={() => setPeriodPickerOpen(false)}
                  options={[
                    { label: tr('pages.bookingSystem.Contact.last30Days'), value: '30' },
                    { label: tr('pages.bookingSystem.Contact.last60Days'), value: '60' },
                    { label: tr('pages.bookingSystem.Contact.last90Days'), value: '90' },
                    { label: tr('pages.bookingSystem.Contact.allTime'), value: 'all' },
                  ]}
                  value={selectedPeriod}
                  matchWidth={false}
                  onSelect={(v) => {
                    setSelectedPeriod(v);
                    setPeriodPickerOpen(false);
                  }}
                />

                {/* Client History Stats - Loading state and real data */}
                <IonCard className={styles.historyStatsList}>
                  <IonList>
                    <>
                      {/* Services Performed Stat */}
                      <IonItem lines="none" className={styles.historyStatItem}>
                        <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                          <IonIcon icon={bagCheckOutline} />
                        </IonText>
                        <IonLabel className={styles.historyStatLabel}>
                          <IonText className={styles.historyStatTitle}>{tr('pages.bookingSystem.Contact.totalServices')}</IonText>
                          <IonText className={styles.historyStatValue}>{historyStats.totalServices}</IonText>
                        </IonLabel>
                      </IonItem>

                      {/* Total Spent Stat */}
                      <IonItem lines="none" className={styles.historyStatItem}>
                        <IonText slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                          <IonIcon icon={cashOutline} />
                        </IonText>
                        <IonLabel className={styles.historyStatLabel}>
                          <IonText className={styles.historyStatTitle}>{tr('pages.bookingSystem.Contact.totalSpent')}</IonText>
                          <IonText className={styles.historyStatValue}>${historyStats.totalSpent.toFixed(2)}</IonText>
                        </IonLabel>
                      </IonItem>
                    </>
                  </IonList>
                </IonCard>
              </IonList>
            </IonCard>


            {/* Client Notes Section */}
            <IonCard className={styles.notesSectionContainer}>
              <IonList>
                <IonItem lines="none" className={styles.sectionTitle}>
                  <IonIcon icon={documentTextOutline} slot="start" className={styles.sectionTitleIcon} />
                  <IonLabel>{tr('pages.bookingSystem.Contact.notes')}</IonLabel>
                  <IonButton
                    fill="clear"
                    size="small"
                    className={styles.iconButton}
                    onClick={() => {
                      setShowNotes(!showNotes);
                    }}
                  >
                    <IonIcon
                      icon={showNotes ? closeCircle : createOutline}
                      color={(contactDetails?.notes?.[0]?.content || contactDetails?.notes?.[1]?.content || contactDetails?.notes?.[2]?.content) ? "success" : "primary"}
                    />
                  </IonButton>
                </IonItem>

                {showNotes && (
                  <IonItem lines="none" className={styles.notesContainer}>
                    {isEditingNotes ? (
                      // Edit mode
                      <IonCol className={styles.notesEditContainer}>
                        <IonRow className={styles.notesTextareaRow}>
                          <IonTextarea
                            className={styles.notesTextarea}
                            value={editedNotes}
                            onIonInput={(e: CustomEvent) => {
                              setEditedNotes(e.detail.value || '');
                            }}
                            placeholder={tr('pages.bookingSystem.Contact.addNotesAboutThisContact')}
                            rows={4}
                            autoGrow={true}
                          ></IonTextarea>
                        </IonRow>
                        <IonRow className={styles.notesButtonRow}>
                          <IonButton
                            size="small"
                            fill="outline"
                            onClick={() => {
                              // Revert to original notes and exit edit mode
                              setEditedNotes('');
                              setIsEditingNotes(false);
                              // Don't try to access sessionStorage
                            }}
                          >{tr('pages.bookingSystem.Contact.cancel')}
                          </IonButton>
                          <IonButton
                            size="small"
                            onClick={handleSaveNotes}
                          >{tr('pages.bookingSystem.Contact.save')}
                          </IonButton>
                        </IonRow>
                      </IonCol>
                    ) : (
                      // View mode
                      <IonCol className={styles.notesViewContainer}>
                        {(contactDetails?.notes?.[0]?.content || contactDetails?.notes?.[1]?.content || contactDetails?.notes?.[2]?.content) ? (
                          <IonList className={styles.notesContentContainer}>
                            {/* Note 1 */}
                            {contactDetails?.notes?.[0]?.content && (
                              <IonItem className={styles.individualNoteContainer} lines="none">
                                <IonLabel className={styles.noteLabel}>
                                  <IonRow className={styles.noteHeader}>
                                    <IonCol size="8">
                                      <IonText className={styles.noteTitle}>
                                        {getTimestampFromCustomFields(contactDetails, 'notes1') ?
                                          formatDateLong(getTimestampFromCustomFields(contactDetails, 'notes1')) :
                                          tr('pages.bookingSystem.Contact.noTimestamp')}
                                      </IonText>
                                    </IonCol>
                                    <IonCol size="4">
                                      <IonButtons className={styles.noteActions}>
                                        <IonButton
                                          size="small"
                                          fill="clear"
                                          shape="round"
                                          className={styles.editNotesButton}
                                          onClick={() => handleEditNoteField('notes1')}
                                        >
                                          <IonIcon icon={pencilOutline} slot="end" />
                                        </IonButton>
                                        <IonButton
                                          size="small"
                                          fill="clear"
                                          color="danger"
                                          shape="round"
                                          className={styles.deleteNotesButton}
                                          onClick={() => handleDeleteNoteField('notes1')}
                                        >
                                          <IonIcon icon={trashOutline} slot="end" />
                                        </IonButton>
                                      </IonButtons>
                                    </IonCol>
                                  </IonRow>
                                  <IonText className={styles.notesContent}>
                                    {/* Display the full note content */}
                                    {contactDetails.notes[0].content}
                                  </IonText>
                                </IonLabel>
                              </IonItem>
                            )}

                            {/* Note 2 */}
                            {contactDetails?.notes?.[1]?.content && (
                              <IonItem className={styles.individualNoteContainer} lines="none">
                                <IonLabel className={styles.noteLabel}>
                                  <IonRow className={styles.noteHeader}>
                                    <IonCol size="8">
                                      <IonText className={styles.noteTitle}>
                                        {getTimestampFromCustomFields(contactDetails, 'notes2') ?
                                          formatDateLong(getTimestampFromCustomFields(contactDetails, 'notes2')) :
                                          tr('pages.bookingSystem.Contact.noTimestamp')}
                                      </IonText>
                                    </IonCol>
                                    <IonCol size="4">
                                      <IonButtons className={styles.noteActions}>
                                        <IonButton
                                          size="small"
                                          fill="clear"
                                          shape="round"
                                          className={styles.editNotesButton}
                                          onClick={() => handleEditNoteField('notes2')}
                                        >
                                          <IonIcon icon={pencilOutline} slot="end" />
                                        </IonButton>
                                        <IonButton
                                          size="small"
                                          fill="clear"
                                          color="danger"
                                          className={styles.deleteNotesButton}
                                          onClick={() => handleDeleteNoteField('notes2')}
                                        >
                                          <IonIcon icon={trashOutline} slot="end" />
                                        </IonButton>
                                      </IonButtons>
                                    </IonCol>
                                  </IonRow>
                                  <IonText className={styles.noteContent}>
                                    {/* Display the full note content */}
                                    {contactDetails.notes[1].content}
                                  </IonText>
                                </IonLabel>
                              </IonItem>
                            )}

                            {/* Note 3 */}
                            {contactDetails?.notes?.[2]?.content && (
                              <IonItem className={styles.individualNoteContainer} lines="none">
                                <IonLabel className={styles.noteLabel}>
                                  <IonRow className={styles.noteHeader}>
                                    <IonCol size="8">
                                      <IonText className={styles.noteTitle}>
                                        {getTimestampFromCustomFields(contactDetails, 'notes3') ?
                                          formatDateLong(getTimestampFromCustomFields(contactDetails, 'notes3')) :
                                          tr('pages.bookingSystem.Contact.noTimestamp')}
                                      </IonText>
                                    </IonCol>
                                    <IonCol size="4">
                                      <IonButtons className={styles.noteActions}>
                                        <IonButton
                                          size="small"
                                          fill="clear"
                                          shape="round"
                                          className={styles.editNotesButton}
                                          onClick={() => handleEditNoteField('notes3')}
                                        >
                                          <IonIcon icon={pencilOutline} slot="end" />
                                        </IonButton>
                                        <IonButton
                                          size="small"
                                          fill="clear"
                                          color="danger"
                                          shape="round"
                                          className={styles.deleteNotesButton}
                                          onClick={() => handleDeleteNoteField('notes3')}
                                        >
                                          <IonIcon icon={trashOutline} slot="end" />
                                        </IonButton>
                                      </IonButtons>
                                    </IonCol>
                                  </IonRow>
                                  <IonText className={styles.noteContent}>
                                    {/* Display the full note content */}
                                    {contactDetails.notes[2].content}
                                  </IonText>
                                </IonLabel>
                              </IonItem>
                            )}

                            <IonButton
                              size="small"
                              expand="full"
                              fill="clear"
                              shape="round"
                              onClick={handleAddMoreNotes}
                              className={styles.addMoreNotesButton}
                            >{tr('pages.bookingSystem.Contact.addMoreNotes')}
                            </IonButton>
                          </IonList>
                        ) : (
                          <IonItem lines="none" className={styles.emptyNotesContainer}>
                            <IonLabel className={styles.emptyNotes}>
                              <IonText>{tr('pages.bookingSystem.Contact.noNotesAddedForThisContact')}
                              </IonText>
                              <IonButton
                                size="small"
                                fill="clear"
                                shape="round"
                                onClick={handleAddNewNote}
                              >
                                <IonIcon icon={addCircleOutline} slot="start" />{tr('pages.bookingSystem.Contact.addNotes')}
                              </IonButton>
                            </IonLabel>
                          </IonItem>
                        )}
                      </IonCol>
                    )}
                  </IonItem>
                )}
              </IonList>
            </IonCard>


            {/* Tasks and Reminders Section */}
            <IonCard className={styles.tasksContainer}>
              <IonItem lines="none" className={styles.sectionTitle}>
                <IonIcon icon={checkmarkCircleOutline} slot="start" className={styles.sectionTitleIcon} />
                <IonLabel>{tr('pages.bookingSystem.Contact.tasksReminders')}</IonLabel>
                <IonButton
                  fill="clear"
                  size="small"
                  className={styles.iconButton}
                  onClick={() => setShowTasks(!showTasks)}
                >
                  <IonIcon
                    icon={showTasks ? closeCircle : calendarOutline}
                    color="primary"
                  />
                </IonButton>
              </IonItem>
            </IonCard>


            {showTasks && (
              <IonItem lines="none" className={styles.tasksContainer}>
                <IonCol className={styles.tasksViewContainer}>
                  {tasks.length > 0 ? (
                    <>
                      <IonList className={styles.tasksList}>
                        {tasks.map((task, index) => (
                          <IonItem key={task.id || `task-${index}`} className={styles.taskItem}>
                            <IonIcon
                              icon={task.status === 'completed' ? checkmarkCircleOutline : timeOutline}
                              color={task.status === 'completed' ? 'success' :
                                (task.priority === 'high' ? 'danger' :
                                  task.priority === 'medium' ? 'warning' : 'medium')}
                              slot="start"
                              className={styles.taskStatusIcon}
                            />
                            <IonLabel className={styles.taskContent}>
                              <h3 className={`${styles.taskTitle} ${task.status === 'completed' ? styles.completedTask : ''}`}>
                                {task.title}
                              </h3>
                              {task.dueDate && (
                                <p className={styles.taskDueDate}>
                                  <IonIcon icon={calendarOutline} className={styles.taskDateIcon} />{tr('pages.bookingSystem.Contact.due')} {new Date(task.dueDate).toLocaleDateString(intlLocale())}
                                </p>
                              )}
                            </IonLabel>
                            {task.status !== 'completed' && (
                              <IonButton
                                fill="clear"
                                size="small"
                                className={styles.completeTaskButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCompleteTask(task.id);
                                }}
                              >{tr('pages.bookingSystem.Contact.done')}
                              </IonButton>
                            )}
                          </IonItem>
                        ))}
                      </IonList>
                      <IonItem lines="none" className={styles.addTaskButtonItem}>
                        <IonButton
                          size="small"
                          className={styles.addTaskButton}
                          onClick={() => setShowAddTaskModal(true)}
                        >
                          <IonIcon icon={addCircleOutline} slot="start" />{tr('pages.bookingSystem.Contact.addTask')}
                        </IonButton>
                      </IonItem>
                    </>
                  ) : (
                    <IonItem lines="none" className={styles.emptyTasksItem}>
                      <IonLabel className="ion-text-center">

                        <IonText color="medium" className={styles.emptyTasks}>{tr('pages.bookingSystem.Contact.noTasksOrRemindersForThis')}
                        </IonText>
                        <IonButton
                          size="small"
                          onClick={() => setShowAddTaskModal(true)}
                        >{tr('pages.bookingSystem.Contact.addTask')}
                        </IonButton>
                      </IonLabel>
                    </IonItem>
                  )}
                </IonCol>
              </IonItem>
            )}
          </>
        )}


        {/* Add Task Modal */}
        {/* Only render TaskModal when it's open */}
        {showAddTaskModal && (
          <TaskModal
            isOpen={showAddTaskModal}
            onClose={() => setShowAddTaskModal(false)}
            onSave={handleAddTask}
            contactId={contact?.id}
            title={tr('pages.bookingSystem.Contact.addTask')}
          />
        )}

        {/* Booking Modal */}
        {isBookingModalOpen && (
          <Booking
            isOpen={isBookingModalOpen}
            onClose={() => setIsBookingModalOpen(false)}
          />
        )}

        {/* Booking Details Modal */}
        {showBookingDetailsModal && (
          <BookingDetails
            isOpen={showBookingDetailsModal}
            bookingId={selectedBookingId}
            onClose={() => {
              setShowBookingDetailsModal(false);
              setSelectedBookingId(undefined);
            }}
          />
        )}

        {/* All bookings Modal */}
        <IonModal
          isOpen={showAllbookingsModal}
          onDidDismiss={() => {
            setShowAllbookingsModal(false);
            setShowHistoryDateSearch(false);
            setHistoryDateFilter('');
            applyHistoryProgress(0, false);
          }}
          className={styles.bookingsModal}
        >
          <BasePageWithHeader
            ref={historyHeaderRef}
            title={tr('pages.bookingSystem.Contact.bookingHistory')}
            toolbarClassName={styles.customToolbar}
            smallTitleClassName={styles.bookingsModalSmallTitle}
            startButtons={
              <IonButton fill="clear" className={styles.historyBackButton} onClick={() => setShowAllbookingsModal(false)}>
                <IonIcon icon={chevronBack} slot="icon-only" />
              </IonButton>
            }
            endButtons={
              <IonButton
                ref={historyDateSearchAnchorRef}
                fill="clear"
                className={`${styles.historySearchButton} ${historyDateFilter ? styles.historySearchButtonActive : ''}`}
                onClick={() => setShowHistoryDateSearch(prev => !prev)}
              >
                <IonIcon icon={calendarOutline} slot="icon-only" />
              </IonButton>
            }
          >
            <div ref={historyPickerPortalRef} className={styles.pickerPortalRoot} />
            <PortalDropdown
              isOpen={showHistoryDateSearch}
              anchorRef={historyDateSearchAnchorRef}
              portalRef={historyPickerPortalRef}
              onClose={() => setShowHistoryDateSearch(false)}
              options={historyDateOptions}
              value={historyDateFilter}
              onSelect={(v) => { setHistoryDateFilter(v); setShowHistoryDateSearch(false); }}
              searchable
              emptyText={tr('pages.bookingSystem.Contact.noDatesFound')}
              align="right"
              matchWidth={false}
            />
            <IonContent scrollY={false} className={styles.bookingsModalContent}>
              <IonGrid
                ref={historyScrollRef}
                className={styles.bookingsScrollGrid}
                onScroll={(e) => {
                  const el = e.target as HTMLElement;
                  handleHistoryScroll(el.scrollTop || 0);
                  const deltaFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                  if (deltaFromBottom < 200 && parentHasMoreHistory && !parentIsLoadingMoreHistory) {
                    parentLoadMoreHistory();
                  }
                }}
              >
                <div className={styles.titleCardWrapper}>
                  <TitleCard
                    ref={historyTitleCardRef}
                    title={tr('pages.bookingSystem.Contact.bookingHistory')}
                    cardClassName={styles.historyTitleCard}
                    titleClassName={styles.historyTitleText}
                    titleContainerClassName={styles.historyTitleContainer}
                  />
                </div>

                {filteredPastBookings.length > 0 ? (
                  filteredPastBookings.map((booking, index) => {
                    const dateStr = booking.date || '';
                    const dateParts = dateStr.split('-');
                    const bookingDate = dateParts.length === 3
                      ? new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]))
                      : new Date(dateStr);

                    const bookingYear = bookingDate.getFullYear();
                    const prevYear = index > 0
                      ? (() => {
                        const prevDateStr = filteredPastBookings[index - 1].date || '';
                        const prevParts = prevDateStr.split('-');
                        const prevDate = prevParts.length === 3
                          ? new Date(parseInt(prevParts[0]), parseInt(prevParts[1]) - 1, parseInt(prevParts[2]))
                          : new Date(prevDateStr);
                        return prevDate.getFullYear();
                      })()
                      : null;
                    const showYearDivider = index === 0 || bookingYear !== prevYear;

                    return (
                      <React.Fragment key={booking.id || `booking-history-${index}`}>
                        {showYearDivider && (
                          <IonItem lines="none" className={styles.historyYearDivider}>
                            <IonText className={styles.historyYearDividerText}>{bookingYear}</IonText>
                          </IonItem>
                        )}
                        <IonCard
                          className={styles.agendaContainer}
                          button
                          onClick={() => {
                            setSelectedBookingId(booking.id);
                            setShowBookingDetailsModal(true);
                          }}
                        >
                          <IonItem className={styles.agendaItem} lines="none" detail={false}>
                            <IonCol size="auto" className={styles.dateCol}>
                              <IonText className={styles.dayNumber}>{getDayNumber(bookingDate)}</IonText>
                              <IonText className={styles.monthText}>{getMonthAbbr(bookingDate)}</IonText>
                            </IonCol>

                            <IonCol className={styles.bookingCol}>
                              <IonText
                                className={styles.serviceType}
                                style={{ color: getServiceColor(booking.serviceType as ServiceType) }}
                              >
                                {translateDynamic(booking.serviceType)}
                              </IonText>
                              <IonText className={styles.bookingDetails}>
                                {getDayName(bookingDate)} • {formatTime(formatTimeToString(booking.startTime))} • {booking.duration || '1h'}
                              </IonText>
                              <IonText className={styles.resourceInfo}>
                                {booking.resource || tr('pages.bookingSystem.Contact.noLocation')} • {booking.staff || tr('pages.bookingSystem.Contact.unassignedStaff')}
                              </IonText>
                            </IonCol>
                          </IonItem>
                        </IonCard>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <IonItem lines="none" className={styles.historyEmptyItem}>
                    <IonLabel>
                      <IonText color="medium" className={styles.emptyMessage}>
                        {historyDateFilter ? tr('pages.bookingSystem.Contact.noBookingsFoundForThisDate') : tr('pages.bookingSystem.Contact.noPastBookingsFound')}
                      </IonText>
                    </IonLabel>
                  </IonItem>
                )}
                {parentIsLoadingMoreHistory && (
                  <IonItem lines="none" className={styles.historyLoadMoreItem}>
                    <IonSpinner name="dots" />
                  </IonItem>
                )}
              </IonGrid>
            </IonContent>
          </BasePageWithHeader>
        </IonModal>

        <IonModal
          isOpen={showInvoicesModal}
          onDidDismiss={() => setShowInvoicesModal(false)}
          onWillDismiss={() => setShowInvoicesModal(false)}
          canDismiss={true}
          backdropDismiss={true}
          keepContentsMounted={false}
          handle={false}
          className={styles.bookingsModal}
        >
          <InvoiceList onClose={() => setShowInvoicesModal(false)} customerId={zohoContactId} />
        </IonModal>
      </IonList >
    );
  };

export default Contact;






